import React, { useEffect, useState, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import {
  ArrowUpRight, ShieldCheck, Wallet, Activity, Menu, X, LockKeyhole,
  LogOut, Clock3, CheckCircle2, Copy, Bell, MessageCircle, Users,
  Settings as SettingsIcon, ClipboardList, TrendingUp, AlertCircle,
} from "lucide-react";
import "./App.css";
import { NewsSection, NewsPage, NewsDetail, AdminNews, NotificationBell, DashboardNews } from "./News";
import { SupportPanel, AdminSupport, useSupportUnread } from "./Support";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const fallbackPlans = [
  { plan_id: "core", name: "Core Horizon", min_amount: 100, max_amount: 4999, duration_days: 30, daily_rate_percent: 0.15, status: "Active" },
  { plan_id: "growth", name: "Growth Atlas", min_amount: 5000, max_amount: 24999, duration_days: 90, daily_rate_percent: 0.25, status: "Active" },
  { plan_id: "private", name: "Private Circle", min_amount: 25000, max_amount: 100000, duration_days: 180, daily_rate_percent: 0.4, status: "Paused" },
];

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const short = (v) => (v || "").toString().slice(0, 12);
const fmtDT = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString([], { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const normalizeTelegramLink = (raw) => {
  const v = (raw || "").trim();
  if (!v) return "#";
  if (/^(https?:|tg:)/i.test(v)) return v;
  // Accept bare @username, username, or t.me/username
  const cleaned = v.replace(/^@/, "").replace(/^t\.me\//i, "");
  return `https://t.me/${cleaned}`;
};

// Portfolio Signal value — grows on a 3-hour cycle: +0.5%, +1%, +0.7%, then repeats.
// Deterministic across all visitors by anchoring to a fixed epoch.
const PORTFOLIO_BASE = 24860.40 * 9; // 223,743.60
const PORTFOLIO_EPOCH = Date.UTC(2026, 9, 15, 0, 0, 0); // 15 Oct 2026 00:00 UTC
const CYCLE = [0.005, 0.010, 0.007];

function computePortfolioValue(nowMs) {
  const elapsedMs = Math.max(0, nowMs - PORTFOLIO_EPOCH);
  const fullHours = Math.floor(elapsedMs / 3_600_000);
  let value = PORTFOLIO_BASE;
  for (let i = 0; i < fullHours; i++) value *= 1 + CYCLE[i % 3];
  return value;
}

function PortfolioSignalValue() {
  const [value, setValue] = useState(() => computePortfolioValue(Date.now()));
  useEffect(() => {
    // Re-check every minute so the UI updates as soon as a new hour rolls over.
    const t = setInterval(() => setValue(computePortfolioValue(Date.now())), 60_000);
    return () => clearInterval(t);
  }, []);
  const formatted = `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="signal-footer" data-testid="portfolio-signal-value">
      <span>Current value</span>
      <b>{formatted}</b>
    </div>
  );
}

// Deterministic hash → [5, 15] band for the "24h activity" chip (fresh value once per calendar day, same for every visitor).
function dailyActivityPercent() {
  const d = new Date();
  const seed = d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
  // Simple LCG-style hash so consecutive days don't drift linearly
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  const r = x - Math.floor(x);
  return 5 + r * 10; // 5.00 – 15.00
}

function PortfolioLiveReturn() {
  const [pct, setPct] = useState(() => 5 + Math.random() * 10);
  useEffect(() => {
    const t = setInterval(() => setPct(5 + Math.random() * 10), 1000);
    return () => clearInterval(t);
  }, []);
  return <strong data-testid="portfolio-return-pct">+{pct.toFixed(2)}%</strong>;
}

function PortfolioDailyActivity() {
  const [pct, setPct] = useState(() => dailyActivityPercent());
  useEffect(() => {
    // Re-check every 5 min so the chip updates automatically after midnight UTC without a reload.
    const t = setInterval(() => setPct(dailyActivityPercent()), 5 * 60_000);
    return () => clearInterval(t);
  }, []);
  return <span data-testid="portfolio-activity-pct">+{pct.toFixed(1)}%</span>;
}

function CopyField({ value, label, testid }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="muted-dash">—</span>;
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(value));
    } catch {
      const ta = document.createElement("textarea");
      ta.value = String(value); document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <span className="copy-field" data-testid={testid || "copy-field"}>
      {label && <small className="copy-label">{label}</small>}
      <span className="copy-value" title={value}>{value}</span>
      <button type="button" className="copy-btn" onClick={copy} aria-label={`Copy ${label || "value"}`} data-testid={testid ? `${testid}-btn` : "copy-field-btn"}>
        {copied ? <CheckCircle2 size={13} /> : <Copy size={12} />}
        <span>{copied ? "Copied" : "Copy"}</span>
      </button>
    </span>
  );
}
const greetByHour = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return "Good morning";
  if (h >= 12 && h < 17) return "Good afternoon";
  if (h >= 17 && h < 21) return "Good evening";
  return "Good night";
};

// Capture the browser's beforeinstallprompt event once, globally, so any button can trigger it later.
let _deferredInstall = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    _deferredInstall = e;
    window.dispatchEvent(new CustomEvent("nexa:install-available"));
  });
  window.addEventListener("appinstalled", () => {
    _deferredInstall = null;
    window.dispatchEvent(new CustomEvent("nexa:install-done"));
  });
}

function useInstallPrompt() {
  const [available, setAvailable] = useState(!!_deferredInstall);
  const [installed, setInstalled] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  });
  useEffect(() => {
    const on = () => setAvailable(!!_deferredInstall);
    const off = () => { setAvailable(false); setInstalled(true); };
    window.addEventListener("nexa:install-available", on);
    window.addEventListener("nexa:install-done", off);
    return () => {
      window.removeEventListener("nexa:install-available", on);
      window.removeEventListener("nexa:install-done", off);
    };
  }, []);
  const promptInstall = async () => {
    if (!_deferredInstall) return "unavailable";
    _deferredInstall.prompt();
    const choice = await _deferredInstall.userChoice;
    _deferredInstall = null;
    setAvailable(false);
    return choice.outcome;
  };
  const isIOS = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
  return { available, installed, promptInstall, isIOS };
}

function DownloadAppButton({ className = "mini-button", children = "Download app", testid = "download-app-btn" }) {
  const { available, installed, promptInstall, isIOS } = useInstallPrompt();
  const [msg, setMsg] = useState("");
  if (installed) return null;
  const handle = async () => {
    if (available) {
      const outcome = await promptInstall();
      if (outcome !== "accepted") setMsg("Install dismissed. You can try again anytime.");
      return;
    }
    if (isIOS) {
      setMsg("On iPhone, tap the Share icon in Safari, then choose Add to Home Screen.");
    } else {
      setMsg("Open the Nexa Pro site in Chrome, Edge or Brave and tap the menu → Install app / Add to Home screen.");
    }
    setTimeout(() => setMsg(""), 6000);
  };
  return (
    <>
      <button type="button" className={className} onClick={handle} data-testid={testid}>
        {children}
      </button>
      {msg && <div className="install-hint" data-testid="install-hint">{msg}</div>}
    </>
  );
}

function Logo({ dark = false }) {
  return (
    <Link className="logo brand-logo" to="/" data-testid="brand-logo" aria-label="Nexa Pro home">
      <img src="/brand/nexa-pro-logo.png" alt="Nexa Pro" className="brand-logo-img" />
    </Link>
  );
}

function Header({ user, onLogout, onOpenLogin }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="topbar">
      <Logo />
      <nav className={open ? "nav open" : "nav"}>
        <Link data-testid="nav-plans" to="/plans" onClick={() => setOpen(false)}>Plans</Link>
        <Link data-testid="nav-news" to="/news" onClick={() => setOpen(false)}>News</Link>
        <Link data-testid="nav-about" to="/about" onClick={() => setOpen(false)}>About</Link>
        <Link data-testid="nav-faq" to="/faq" onClick={() => setOpen(false)}>FAQ</Link>
        <DownloadAppButton className="nav-download-btn" testid="nav-download-app">
          <ArrowUpRight size={13} /> Get the app
        </DownloadAppButton>
        {user ? (
          <Link data-testid="nav-login" className="nav-cta" to="/dashboard" onClick={() => setOpen(false)}>
            Dashboard <ArrowUpRight size={15} />
          </Link>
        ) : (
          <button data-testid="nav-login" className="nav-cta nav-cta-btn" onClick={() => { setOpen(false); onOpenLogin(); }}>
            Sign in <ArrowUpRight size={15} />
          </button>
        )}
      </nav>
      {user && (
        <button className="icon-button" data-testid="logout-button" onClick={onLogout}>
          <LogOut size={16} />
        </button>
      )}
      <button className="menu-btn" data-testid="mobile-menu-button" onClick={() => setOpen(!open)}>
        {open ? <X /> : <Menu />}
      </button>
    </header>
  );
}

function useAuth() {
  const [user, setUser] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    // CRITICAL: skip /auth/me during OAuth callback so AuthCallback can exchange first
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoaded(true); return;
    }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);
  return { user, setUser, loaded };
}

// ---------------- Public pages ----------------
function Home({ user, onOpenLogin }) {
  return (
    <>
      <section className="hero">
        <div className="hero-copy">
          <div className="eyebrow"><span className="pulse" /> DIGITAL WEALTH, MORE INTENTIONAL</div>
          <h1>Build your next<br /><em>financial chapter.</em></h1>
          <p>One clear place to explore investment plans, track your portfolio, and move with greater confidence.</p>
          <div className="hero-actions">
            {user ? (
              <Link data-testid="hero-start-button" className="button primary" to="/dashboard">
                Go to dashboard <ArrowUpRight size={16} />
              </Link>
            ) : (
              <button data-testid="hero-start-button" className="button primary" onClick={onOpenLogin}>
                Start exploring <ArrowUpRight size={16} />
              </button>
            )}
            <Link data-testid="hero-plans-link" className="button ghost" to="/plans">View plans</Link>
          </div>
          <div className="hero-note"><ShieldCheck size={15} /> Manual review on every deposit & withdrawal</div>
        </div>
        <div className="hero-visual">
          <div className="orbit orbit-one" />
          <div className="orbit orbit-two" />
          <div className="signal-card">
            <div className="signal-top"><span>PORTFOLIO SIGNAL</span><Activity size={16} /></div>
            <PortfolioLiveReturn />
            <span className="signal-sub">Target range · 365 days</span>
            <div className="chart">{[25, 43, 37, 64, 52, 78, 96].map((h, i) => <i key={i} style={{ height: `${h}%` }} />)}</div>
            <PortfolioSignalValue />
          </div>
          <div className="floating-chip chip-one"><span className="chip-dot green" />24h activity <b><PortfolioDailyActivity /></b></div>
          <div className="floating-chip chip-two"><ShieldCheck size={14} /> verified custody</div>
        </div>
      </section>
      <section className="trust-row">
        <div><b>01</b><span>Clear plan parameters</span></div>
        <div><b>02</b><span>Human-reviewed transfers</span></div>
        <div><b>03</b><span>Transparent risk language</span></div>
      </section>
      <section className="section plans-preview">
        <div className="section-heading">
          <div>
            <div className="eyebrow">CURATED PATHS</div>
            <h2>Plans with room<br />for a point of view.</h2>
          </div>
          <Link className="text-link" data-testid="plans-section-link" to="/plans">Explore all plans <ArrowUpRight size={15} /></Link>
        </div>
        <PlanCards compact />
      </section>
      <NewsSection />
      <section className="dark-band">
        <div className="section-heading">
          <div>
            <div className="eyebrow mint">THE NEXA METHOD</div>
            <h2>More visibility.<br /><span>Less noise.</span></h2>
          </div>
          <p>From your first deposit request to a completed withdrawal, the important moments stay easy to see.</p>
        </div>
        <div className="method-grid">
          {[
            ["01", "Choose your lane", "Compare plan ranges, timelines, and target information before you commit."],
            ["02", "Fund with clarity", "Submit your USDT transfer with the configured memo. Every request starts pending."],
            ["03", "Stay in control", "Watch activity, request withdrawals, and reach support from one dashboard."],
          ].map((x) => (
            <div key={x[0]}><b>{x[0]}</b><h3>{x[1]}</h3><p>{x[2]}</p></div>
          ))}
        </div>
      </section>
    </>
  );
}

function PlanCards({ compact = false }) {
  const [plans, setPlans] = useState(fallbackPlans);
  useEffect(() => { api.get("/plans").then((r) => r.data?.length && setPlans(r.data)).catch(() => {}); }, []);
  return (
    <div className="plan-grid">
      {plans.map((p, i) => {
        const totalPct = p.daily_rate_percent * p.duration_days;
        const totalMax = p.max_amount * totalPct / 100;
        const totalMin = p.min_amount * totalPct / 100;
        return (
          <article className={`plan-card ${i === 1 ? "featured" : ""}`} key={p.plan_id} data-testid={`plan-card-${p.plan_id}`}>
            <div className="plan-top">
              <div className="plan-head-left">
                <span className="plan-logo-round" aria-hidden="true">
                  {p.logo_path
                    ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.logo_path}`} alt="" />
                    : <b>{p.name?.charAt(0) || "N"}</b>}
                </span>
                <span className="plan-index">0{i + 1}</span>
              </div>
              <span className={p.status === "Active" ? "status active" : "status paused"}>{p.status}</span>
            </div>
            <h3>USDT Staking</h3>
            <p className="plan-sub">{p.name}</p>
            <p className="plan-return">{p.daily_rate_percent}% daily · {totalPct.toFixed(1)}% total over {p.duration_days} days</p>
            <div className="plan-meta">
              <span>From <b>{money(p.min_amount)}</b></span>
              <span>Up to <b>{money(p.max_amount)}</b></span>
              <span>Duration <b>{p.duration_days} days</b></span>
            </div>
            <div className="plan-total" data-testid={`plan-total-${p.plan_id}`}>
              <small>TOTAL EXPECTED EARNING</small>
              <b>{money(totalMin)} – {money(totalMax)}</b>
              <span>on {money(p.min_amount)} – {money(p.max_amount)} invested</span>
            </div>
            {!compact && (
              <Link data-testid={`plan-invest-${p.plan_id}`} className="button primary" to="/login">
                Select plan <ArrowUpRight size={15} />
              </Link>
            )}
          </article>
        );
      })}
    </div>
  );
}

function LoginModal({ open, onClose }) {
  const [refCode, setRefCode] = useState("");
  const [error, setError] = useState("");
  const [mode, setMode] = useState("login"); // "login" | "signup"
  useEffect(() => {
    if (!open) return;
    // Capture ref from ?ref= if present, otherwise from previously-stored localStorage
    const urlRef = new URLSearchParams(window.location.search).get("ref");
    const saved = urlRef || localStorage.getItem("nexa_ref");
    if (saved) { setRefCode(saved.toUpperCase()); setMode("signup"); }
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose]);
  if (!open) return null;
  const switchMode = (next) => { setMode(next); setError(""); };
  const google = () => {
    if (mode === "signup") {
      // Referral code is OPTIONAL now; only save it if the user typed one
      const code = refCode.trim().toUpperCase();
      if (code) {
        localStorage.setItem("nexa_ref", code);
      } else {
        localStorage.removeItem("nexa_ref");
      }
    } else {
      // Login flow — clear any stale referral so backend doesn't re-apply it
      localStorage.removeItem("nexa_ref");
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const isSignup = mode === "signup";
  return (
    <div className="login-modal-backdrop" onClick={onClose} data-testid="login-modal">
      <div className="login-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} data-testid="login-modal-close" aria-label="Close">
          <X size={18} />
        </button>
        <div className="login-modal-left">
          <img src="/brand/nexa-pro-logo.png" alt="Nexa Pro" className="login-modal-brand" />
          <div className="eyebrow mint">NEXA PRO ACCESS</div>
          <h2>Your clearer<br /><em>next move.</em></h2>
          <p>Private, thoughtful infrastructure for people building toward more.</p>
          <ul className="login-perks">
            <li><CheckCircle2 size={14} /> Instant onboarding with Google</li>
            <li><CheckCircle2 size={14} /> Unique 8-digit User ID</li>
            <li><CheckCircle2 size={14} /> Daily earnings on active stakes</li>
          </ul>
        </div>
        <div className="login-modal-right">
          <div className="auth-tabs" role="tablist">
            <button role="tab" data-testid="login-tab"
              className={`auth-tab ${!isSignup ? "active" : ""}`}
              aria-selected={!isSignup} onClick={() => switchMode("login")}>Log In</button>
            <button role="tab" data-testid="signup-tab"
              className={`auth-tab ${isSignup ? "active" : ""}`}
              aria-selected={isSignup} onClick={() => switchMode("signup")}>Sign Up</button>
          </div>
          <h3 data-testid="auth-mode-title">{isSignup ? "Create your Nexa Pro account" : "Welcome back to Nexa Pro"}</h3>
          <p>{isSignup
            ? "New here? Enter a referral code if you have one, then continue with Google."
            : "Already have an account? Just continue with Google to sign in."}</p>
          {error && <div className="error-box" data-testid="ref-error">{error}</div>}
          {isSignup && (
            <label className="ref-label" data-testid="ref-code-label">Referral code <span className="opt">(optional)</span>
              <input data-testid="modal-ref-code-input" value={refCode}
                onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                placeholder="e.g. NEXAPRO (optional)" maxLength={12} />
            </label>
          )}
          <button data-testid="google-login-button" onClick={google} className="google-button big">
            <span>G</span> {isSignup ? "Sign Up with Google" : "Continue with Google"} <ArrowUpRight size={15} />
          </button>
          <div className="login-divider"><span>or</span></div>
          {isSignup ? (
            <p className="tiny-note">Already have an account?{" "}
              <button type="button" className="link-inline" data-testid="switch-to-login" onClick={() => switchMode("login")}>Log in instead</button>
            </p>
          ) : (
            <p className="tiny-note">Don't have an account?{" "}
              <button type="button" className="link-inline" data-testid="switch-to-signup" onClick={() => switchMode("signup")}>Sign up with a referral code</button>
            </p>
          )}
          <p className="fine-print">By continuing, you acknowledge that digital asset activity involves risk and target returns are not guaranteed.</p>
        </div>
      </div>
    </div>
  );
}

function Public({ children, user, setUser }) {
  const [loginOpen, setLoginOpen] = useState(false);
  return (
    <div className="site-shell">
      <Header user={user} onLogout={async () => { await api.post("/auth/logout"); setUser(null); }} onOpenLogin={() => setLoginOpen(true)} />
      {typeof children === "function" ? children({ openLogin: () => setLoginOpen(true) }) : children}
      <footer>
        <Logo />
        <span>© 2026 Nexa Pro · Built for clearer decisions</span>
        <div>
          <Link to="/news" data-testid="footer-news-link">News</Link>
          <Link to="/about">Risk disclosure</Link>
          <Link to="/faq">Help center</Link>
        </div>
        <Link to="/admin-login" className="footer-admin-link" data-testid="footer-admin-link">Admin access</Link>
      </footer>
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}

// ---------------- Auth Callback (handles #session_id on any route) ----------------
function AuthCallback({ setUser }) {
  const nav = useNavigate();
  const processed = React.useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams(window.location.hash.replace("#", "?"));
    const session_id = params.get("session_id");
    if (!session_id) { nav("/login"); return; }
    const ref = localStorage.getItem("nexa_ref") || undefined;
    api.post("/auth/session", { session_id, ref_code: ref })
      .then((r) => {
        setUser(r.data.user);
        localStorage.removeItem("nexa_ref");
        if (r.data.is_new_user) {
          sessionStorage.setItem("nexa_show_welcome", r.data.user.user_id);
        }
        window.history.replaceState({}, "", "/dashboard");
        nav("/dashboard");
      })
      .catch((err) => {
        const detail = err.response?.data?.detail;
        const msg = detail ? encodeURIComponent(detail) : "session";
        nav(`/login?error=${msg}`);
      });
  }, [nav, setUser]);
  return <div className="loading">Signing you in…</div>;
}

// ---------------- User Login (Google only) ----------------
function Login({ setUser }) {
  const [error, setError] = useState("");
  const loc = useLocation();
  const [params] = useSearchParams();

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) localStorage.setItem("nexa_ref", ref.toUpperCase());
    const err = params.get("error");
    if (err === "session") setError("Google session could not be verified. Please try again.");
    else if (err) setError(decodeURIComponent(err));
  }, [params]);

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main className="auth-page">
      <div className="auth-aside">
        <Logo />
        <div>
          <div className="eyebrow mint">NEXA PRO ACCESS</div>
          <h1>Your clearer<br /><em>next move.</em></h1>
          <p>Private, thoughtful infrastructure for people building toward more.</p>
        </div>
      </div>
      <div className="auth-panel">
        <Link className="back-link" to="/">← Back to home</Link>
        <div className="auth-content">
          <div className="eyebrow">WELCOME IN</div>
          <h2>Sign in to Nexa Pro</h2>
          <p>Use Google to continue to your personal dashboard.</p>
          {error && <div className="error-box" data-testid="auth-error">{error}</div>}
          <button data-testid="google-login-button" onClick={google} className="google-button">
            <span>G</span> Continue with Google <ArrowUpRight size={15} />
          </button>
          <p className="fine-print">By continuing, you acknowledge that digital asset activity involves risk and target returns are not guaranteed.</p>
        </div>
      </div>
    </main>
  );
}

// ---------------- Admin Login (separate route) ----------------
function AdminLogin({ setUser }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/auth/admin-login", form);
      setUser(r.data.user);
      nav("/admin");
    } catch (err) {
      if (err.response?.status === 429) setError(err.response.data.detail || "Locked. Try again later.");
      else setError("Invalid admin credentials.");
    }
  };
  return (
    <main className="auth-page">
      <div className="auth-aside">
        <Logo />
        <div>
          <div className="eyebrow mint">OPERATOR ACCESS</div>
          <h1>Console<br /><em>for owners.</em></h1>
          <p>Secure operations access for manual review, plan management, and platform settings.</p>
        </div>
      </div>
      <div className="auth-panel">
        <Link className="back-link" to="/">← Back to home</Link>
        <div className="auth-content">
          <div className="eyebrow">ADMIN ACCESS</div>
          <h2>Admin console</h2>
          <p>Secure operations access for manual review and settings.</p>
          {error && <div className="error-box" data-testid="auth-error">{error}</div>}
          <form onSubmit={submit} className="form-stack">
            <label>Email
              <input data-testid="admin-email-input" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </label>
            <label>Password
              <input data-testid="admin-password-input" type="password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </label>
            <button data-testid="admin-login-button" className="button primary">
              Enter admin panel <LockKeyhole size={15} />
            </button>
          </form>
          <p className="fine-print">Restricted to operators of Nexa Pro</p>
        </div>
      </div>
    </main>
  );
}

// ---------------- Dashboard shell ----------------
function Metric({ icon, label, value, accent, sub }) {
  return (
    <div className={accent ? "metric accent" : "metric"} data-testid={`metric-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <div className="metric-icon">{icon}</div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{sub || (accent ? "Available now" : "Across your account")}</small>
    </div>
  );
}

const USER_TABS = ["Overview", "My investments", "Deposit", "Withdraw", "Transactions", "News", "Refer & earn", "Help & support", "Profile"];
const ADMIN_TABS = ["Overview", "Users", "Deposits", "Withdrawals", "Investments", "Plans", "News", "Referrals", "Support", "Announce", "Admins", "Settings", "Access", "Audit"];

function Dashboard({ user, setUser, admin = false }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [settings, setSettings] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const supportUnread = useSupportUnread(admin);
  const nav = useNavigate();

  const refresh = useCallback(async () => {
    const r = await api.get(admin ? "/admin/overview" : "/dashboard").catch(() => null);
    if (!r) return nav("/login");
    setData(r.data);
  }, [admin, nav]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (admin || !user?.user_id) return;
    const flag = sessionStorage.getItem("nexa_show_welcome");
    if (flag && flag === user.user_id) {
      setShowWelcome(true);
      sessionStorage.removeItem("nexa_show_welcome");
    }
  }, [admin, user]);

  if (!data) return <div className="loading">Loading your Nexa view…</div>;

  const s = admin
    ? { balance: data.users, invested: data.active_investments, pending_deposits: data.pending_deposits, pending_withdrawals: data.pending_withdrawals }
    : data.stats;

  return (
    <div className="dashboard">
      <aside className="sidebar">
        <Logo />
        <div className="side-label">{admin ? "Operations" : "Workspace"}</div>
        {(admin ? ADMIN_TABS : USER_TABS).map((x) => (
          <button key={x} data-testid={`sidebar-${x.toLowerCase().replaceAll(" ", "-").replaceAll("&", "and")}`}
            className={tab === x ? "side-link selected" : "side-link"} onClick={() => setTab(x)}>
            {x}
            {admin && x === "Support" && supportUnread > 0 && <span className="side-badge" data-testid="sidebar-support-badge">{supportUnread}</span>}
          </button>
        ))}
        <button className="side-link logout-side" data-testid="dashboard-logout"
          onClick={async () => { await api.post("/auth/logout"); setUser(null); nav("/"); }}>
          <LogOut size={15} /> Log out
        </button>
      </aside>
      <main className="dash-main">
        <div className="dash-top">
          <div>
            <div className="eyebrow">{admin ? "NEXA PRO OPERATIONS" : "PERSONAL OVERVIEW"}</div>
            <h1>{admin ? `${greetByHour()}, operator.` : <>{greetByHour()}, {user?.name?.split(" ")[0] || "investor"}.</>}</h1>
          </div>
          <div className="dash-top-right">
          {!admin && <DownloadAppButton className="dash-get-app" testid="dashboard-get-app"><ArrowUpRight size={13} /> Get the app</DownloadAppButton>}
          {!admin && <NotificationBell />}
          <div className="user-pill">
            <span>{admin ? "AD" : (user?.name || "NP").slice(0, 2).toUpperCase()}</span>
            <div>
              <b data-testid="user-pill-name">{user?.name || "Nexa member"}</b>
              <small data-testid="user-pill-id">{admin ? "Administrator" : "ID · " + (user?.user_id || "10000001")}</small>
            </div>
          </div>
          </div>
        </div>

        {tab === "Overview" && (
          <div className="metric-grid">
            {admin ? (
              <>
                <Metric icon={<Users />} label="Total users" value={data.users} accent sub={`${data.active_users} active`} />
                <Metric icon={<Activity />} label="Active investments" value={data.active_investments} />
                <Metric icon={<Clock3 />} label="Pending deposits" value={data.pending_deposits} sub={money(data.total_deposits_amount) + " approved"} />
                <Metric icon={<ArrowUpRight />} label="Pending withdrawals" value={data.pending_withdrawals} sub={`${data.completed_withdrawals} completed`} />
              </>
            ) : (
              <>
                <Metric icon={<Wallet />} label="Available balance" value={money(s.balance)} accent />
                <Metric icon={<TrendingUp />} label="Active investments" value={money(s.invested)} sub={"Projected " + money(s.projected_earnings)} />
                <Metric icon={<Clock3 />} label="Pending deposits" value={money(s.pending_deposits)} />
                <Metric icon={<ArrowUpRight />} label="Referral earnings" value={money(s.referral_earnings)} sub={`${s.referral_count} referrals`} />
              </>
            )}
          </div>
        )}

        {admin ? (
          <AdminArea tab={tab} data={data} refresh={refresh} />
        ) : (
          <UserArea tab={tab} data={data} settings={settings} refresh={refresh} />
        )}
      </main>
      {showWelcome && !admin && <WelcomeDownloadModal user={user} data={data} onClose={() => setShowWelcome(false)} />}
    </div>
  );
}

function WelcomeDownloadModal({ user, data, onClose }) {
  const refCode = data?.user?.referral_code || user?.user_id;
  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="welcome-modal">
      <div className="panel welcome-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="welcome-close"><X size={18} /></button>
        <div className="welcome-logo"><img src="/brand/nexa-pro-logo.png" alt="Nexa Pro" /></div>
        <div className="eyebrow mint" style={{color:"#2f8fb8"}}>WELCOME TO NEXA PRO</div>
        <h3 style={{fontSize:26,letterSpacing:"-.04em",margin:"8px 0 6px"}}>You're in, {user?.name?.split(" ")[0] || "investor"}.</h3>
        <p className="panel-copy">Your 6-digit Nexa Pro ID is <b data-testid="welcome-user-id">{user?.user_id}</b>. Save it somewhere safe — you'll need it when contacting support.</p>
        {refCode && (
          <p className="panel-copy" style={{marginTop:4}}>Your personal referral code: <b>{data?.user?.referral_code}</b></p>
        )}
        <div className="welcome-cta">
          <DownloadAppButton className="button primary" testid="welcome-download-app">
            <ArrowUpRight size={14} /> Install the Nexa Pro app
          </DownloadAppButton>
          <button type="button" className="button ghost" onClick={onClose} data-testid="welcome-skip">Maybe later</button>
        </div>
        <p className="fine-print" style={{marginTop:16}}>Installing adds a Nexa Pro icon to your home screen so you can open it like a native app — no app store needed.</p>
      </div>
    </div>
  );
}

// ---------------- User Area ----------------
function UserArea({ tab, data, settings, refresh }) {
  if (tab === "Overview") return <UserOverview data={data} />;
  if (tab === "My investments") return <UserInvestments data={data} refresh={refresh} />;
  if (tab === "Deposit") return <DepositPanel settings={settings} refresh={refresh} />;
  if (tab === "Withdraw") return <WithdrawPanel data={data} settings={settings} refresh={refresh} />;
  if (tab === "Transactions") return <TransactionsPanel />;
  if (tab === "News") return <DashboardNews />;
  if (tab === "Refer & earn") return <ReferralPanel />;
  if (tab === "Help & support") return <SupportPanel isAdmin={false} user={data.user} />;
  if (tab === "Profile") return <ProfilePanel user={data.user} />;
  return null;
}

function UserOverview({ data }) {
  const rows = [...(data.deposits || []), ...(data.withdrawals || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 6);
  return (
    <div className="content-grid">
      <section className="panel wide">
        <div className="panel-head">
          <div><div className="eyebrow">ACTIVITY</div><h2>Recent movements</h2></div>
        </div>
        {rows.length ? (
          <div className="activity-list">
            {rows.map((x) => (
              <div className="activity-row" key={x.deposit_id || x.withdrawal_id}>
                <span className="activity-icon"><ArrowUpRight size={15} /></span>
                <div>
                  <b>{x.deposit_id ? "USDT deposit" : "Withdrawal request"}</b>
                  <small>{x.network} · {new Date(x.created_at).toLocaleDateString()}</small>
                </div>
                <strong>{x.deposit_id ? "+" : "-"}{money(x.amount)}</strong>
                <span className={`status ${x.status}`}>{x.status}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state"><Wallet size={25} /><b>No activity yet</b><span>Your verified movements will appear here.</span></div>
        )}
      </section>
      <section className="panel">
        <div className="eyebrow">RISK REMINDER</div>
        <h2>Stay intentional.</h2>
        <p className="panel-copy">Target figures are informational and never guaranteed. Only invest what you can afford to put at risk.</p>
        <Link className="text-link" to="/about">Read disclosure <ArrowUpRight size={15} /></Link>
      </section>
    </div>
  );
}

function LiveEarnings({ inv }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (inv.status !== "active") return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [inv.status]);
  if (inv.status !== "active") return <span>{money(inv.total_earnings ?? inv.earnings_so_far ?? 0)}</span>;
  const start = new Date(inv.start_date).getTime();
  const elapsedDays = Math.min(inv.duration_days, Math.max(0, (now - start) / 86400000));
  const value = inv.amount * (inv.daily_rate_percent / 100) * elapsedDays;
  return <span className="live-earning" data-testid={`live-earning-${inv.investment_id}`}>{money(value)}<i className="tick-dot" /></span>;
}

function MaturityCountdown({ inv }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (inv.status !== "active") return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [inv.status]);
  if (inv.status !== "active") return <span className="matures-badge matured" data-testid={`matures-${inv.investment_id}`}>Matured</span>;
  const start = new Date(inv.start_date).getTime();
  const maturityMs = start + inv.duration_days * 86400000;
  const remaining = maturityMs - now;
  if (remaining <= 0) return <span className="matures-badge matured" data-testid={`matures-${inv.investment_id}`}>Maturing…</span>;
  const days = Math.floor(remaining / 86400000);
  const hours = Math.floor((remaining % 86400000) / 3600000);
  const mins = Math.floor((remaining % 3600000) / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);
  const label = days > 0
    ? `${days}d ${hours}h ${mins}m`
    : hours > 0
      ? `${hours}h ${mins}m ${secs}s`
      : `${mins}m ${secs}s`;
  const totalMs = inv.duration_days * 86400000;
  const pct = Math.min(100, Math.max(0, ((totalMs - remaining) / totalMs) * 100));
  return (
    <div className="matures-cell" data-testid={`matures-${inv.investment_id}`}>
      <span className="matures-badge">
        <i className="tick-dot" />
        {label}
      </span>
      <span className="matures-bar" aria-hidden="true"><i style={{ width: `${pct}%` }} /></span>
    </div>
  );
}

function UserInvestments({ data, refresh }) {
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState("");
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const invest = async (e) => {
    e.preventDefault();
    try {
      await api.post("/investments", { plan_id: selected.plan_id, amount: Number(amount) });
      setMsg("Investment started successfully."); setAmount(""); setSelected(null); refresh();
    } catch (err) { setMsg(err.response?.data?.detail || "Could not start investment."); }
  };

  return (
    <div className="content-grid stack">
      <section className="panel wide">
        <div className="eyebrow">STAKING</div>
        <h2>Choose a plan</h2>
        <p className="panel-copy">Daily interest is credited to your wallet every day. Your invested principal is returned on the maturity (last) day of the plan.</p>
        <div className="plan-inline">
          {plans.map((p) => {
            const isSelected = selected?.plan_id === p.plan_id;
            const logoUrl = p.logo_path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.logo_path}` : null;
            return (
              <div key={p.plan_id} className={`plan-inline-card ${isSelected ? "chosen" : ""}`}
                data-testid={`invest-plan-${p.plan_id}`}
                onClick={() => p.status === "Active" && setSelected(p)}>
                <div className="plan-top">
                  <div className="plan-head-left">
                    <span className="plan-logo-round" aria-hidden="true">
                      {logoUrl ? <img src={logoUrl} alt="" /> : <b>{p.name?.charAt(0) || "N"}</b>}
                    </span>
                    <b>{p.name}</b>
                  </div>
                  <span className={`status ${p.status === "Active" ? "active" : "paused"}`}>{p.status}</span>
                </div>
                <p className="plan-return">{p.daily_rate_percent}% / day · {p.duration_days} days</p>
                <small>{money(p.min_amount)} – {money(p.max_amount)}</small>
                {isSelected && (
                  <form onSubmit={invest} className="form-stack inline plan-invest-form" onClick={(e) => e.stopPropagation()}>
                    <label>Amount ({money(p.min_amount)} – {money(p.max_amount)})
                      <input data-testid="invest-amount-input" type="number" min={p.min_amount} max={p.max_amount}
                        required value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
                    </label>
                    {amount && !isNaN(Number(amount)) && Number(amount) > 0 && (
                      <div className="invest-projection" data-testid="invest-projection">
                        <div><small>DAILY EARNING (credited daily)</small><b>{money(Number(amount) * p.daily_rate_percent / 100)}</b></div>
                        <div><small>TOTAL EARNING (over {p.duration_days} days)</small><b>{money(Number(amount) * p.daily_rate_percent * p.duration_days / 100)}</b></div>
                        <div><small>PAYOUT ON LAST DAY (Principal + Final Earning)</small><b>{money(Number(amount) + Number(amount) * p.daily_rate_percent / 100)}</b></div>
                      </div>
                    )}
                    <button className="button primary" data-testid="invest-submit-button">
                      Start investment <ArrowUpRight size={15} />
                    </button>
                  </form>
                )}
              </div>
            );
          })}
        </div>
        {msg && <div className="success-box" data-testid="invest-message"><CheckCircle2 /> {msg}</div>}
      </section>

      <section className="panel wide">
        <div className="eyebrow">MY INVESTMENTS</div>
        <h2>Active & history</h2>
        {(data.investments || []).length ? (
          <table className="data-table">
            <thead><tr><th>ID</th><th>Plan</th><th>Amount</th><th>Rate</th><th>Duration</th><th>Matures in</th><th>Earnings</th><th>Status</th></tr></thead>
            <tbody>
              {data.investments.map((x) => (
                <tr key={x.investment_id} data-testid={`investment-row-${x.investment_id}`}>
                  <td>{x.investment_id}</td>
                  <td>{x.plan_name}</td>
                  <td>{money(x.amount)}</td>
                  <td>{x.daily_rate_percent}% / day</td>
                  <td>{x.duration_days}d</td>
                  <td><MaturityCountdown inv={x} /></td>
                  <td><LiveEarnings inv={x} /></td>
                  <td><span className={`status ${x.status}`}>{x.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="empty-state"><b>No investments yet</b><span>Pick a plan above to get started.</span></div>}
      </section>
    </div>
  );
}

function DepositPanel({ settings, refresh }) {
  const [form, setForm] = useState({ amount: "", txid: "", network: "BEP20" });
  const [msg, setMsg] = useState("");
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/deposits", { amount: Number(form.amount), txid: form.txid, network: "BEP20" });
      setMsg("Deposit submitted. Awaiting admin verification.");
      setForm({ amount: "", txid: "", network: "BEP20" });
      refresh();
    } catch (err) { setMsg(err.response?.data?.detail || "Could not submit deposit."); }
  };

  const copyAddress = () => { navigator.clipboard.writeText(settings?.deposit_address || ""); setCopied(true); setTimeout(() => setCopied(false), 1600); };
  const qrUrl = settings?.deposit_qr_path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${settings.deposit_qr_path}` : "";

  return (
    <div className="content-grid">
      <section className="panel wide action-panel">
        <div className="eyebrow">MANUAL REVIEW</div>
        <h2>Submit a deposit</h2>
        <p className="panel-copy">Send USDT on the <b>BEP20 (BNB Smart Chain)</b> network to the address below, then submit the TxID for admin verification.</p>

        <div className="deposit-card">
          {qrUrl && (
            <div className="qr-wrap"><span>SCAN TO PAY</span>
              <img src={qrUrl} alt="Deposit QR" className="qr-image" data-testid="deposit-qr" />
            </div>
          )}
          <div className={qrUrl ? "" : "wide"}><span>NETWORK</span><b data-testid="deposit-network">BEP20 (BNB Smart Chain)</b></div>
          <div className="wide"><span>DEPOSIT ADDRESS</span>
            <b data-testid="deposit-address">{settings?.deposit_address || "Configure in Admin"}</b>
            <button onClick={copyAddress} className="mini-button" data-testid="copy-address-button">
              <Copy size={13} /> {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <div><span>MEMO</span><b data-testid="deposit-memo">{settings?.memo_code}</b></div>
          <div className="wide instructions"><span>INSTRUCTIONS</span><p>{settings?.deposit_instructions}</p></div>
        </div>

        <form className="form-stack" onSubmit={submit}>
          <label>USDT amount (min {money(settings?.min_deposit || 50)})
            <input data-testid="deposit-amount-input" type="number" min="1" required value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </label>
          <label>Transaction hash / TxID
            <input data-testid="deposit-txid-input" required value={form.txid}
              onChange={(e) => setForm({ ...form, txid: e.target.value })} />
          </label>
          <label>Network
            <input data-testid="deposit-network-fixed" value="BEP20 (BNB Smart Chain)" readOnly disabled />
          </label>
          <button data-testid="deposit-submit-button" className="button primary">
            Submit for review <ArrowUpRight size={15} />
          </button>
        </form>
        {msg && <div className="success-box" data-testid="deposit-message"><CheckCircle2 /> {msg}</div>}
      </section>
      <section className="panel">
        <div className="eyebrow">HOW IT WORKS</div>
        <h2>Human review</h2>
        <ol className="steps">
          <li>Scan the QR or copy the deposit address and send USDT strictly on the <b>BEP20</b> network.</li>
          <li>Paste the transaction hash below and submit.</li>
          <li>An admin verifies on-chain and credits your balance.</li>
        </ol>
        <p className="panel-copy small warn"><AlertCircle size={13} /> Sending USDT on any other network (TRC20, ERC20, Polygon…) will result in permanent loss.</p>
      </section>
    </div>
  );
}

function WithdrawPanel({ data, settings, refresh }) {
  const [form, setForm] = useState({ amount: "", address: "", network: "BEP20", account_name: "", bank_account_name: "", bank_account_number: "", bank_ifsc: "" });
  const [msg, setMsg] = useState("");
  const [rate, setRate] = useState(null);
  const [now, setNow] = useState(Date.now());
  const isUpi = form.network === "UPI";
  const isBank = form.network === "BANK";
  const isInr = isUpi || isBank;
  const cooldownUntil = data.user?.withdrawal_cooldown_until ? new Date(data.user.withdrawal_cooldown_until).getTime() : null;
  const cooldownRemaining = cooldownUntil ? cooldownUntil - now : 0;
  const inCooldown = cooldownRemaining > 0;
  useEffect(() => {
    if (!isInr) { setRate(null); return; }
    api.get("/rates/inr").then((r) => setRate(r.data.usdt_inr)).catch(() => setRate(null));
  }, [isInr]);
  useEffect(() => {
    if (!cooldownUntil) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [cooldownUntil]);
  const feePct = Number(settings?.withdrawal_fee_percent || 0);
  const netUsdt = form.amount ? Number(form.amount) * (1 - feePct / 100) : 0;
  const inrValue = isInr && rate && form.amount ? netUsdt * rate : null;
  const setNetwork = (net) => setForm({ ...form, network: net, address: "" });
  const fmtCountdown = (ms) => {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const d = Math.floor(totalSec / 86400);
    const h = Math.floor((totalSec % 86400) / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (d > 0) return `${d}d ${h}h ${m}m ${s}s`;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    return `${m}m ${s}s`;
  };
  const submit = async (e) => {
    e.preventDefault();
    if (inCooldown) { setMsg("A cooldown is active after your previous rejected withdrawal."); return; }
    const amt = Number(form.amount);
    const available = Number(data.stats?.balance || 0);
    if (!amt || amt <= 0) { setMsg("Enter a valid amount."); return; }
    if (amt > available) { setMsg(`Insufficient balance. Available: ${money(available)}`); return; }
    if (isUpi && !form.account_name.trim()) { setMsg("Please enter the UPI account holder name."); return; }
    if (isBank) {
      if (!form.bank_account_name.trim() || !form.bank_account_number.trim() || !form.bank_ifsc.trim()) {
        setMsg("Bank name, account number, and IFSC are required for bank transfer.");
        return;
      }
    }
    try {
      const payload = { amount: Number(form.amount), address: form.address, network: form.network };
      if (isUpi) payload.account_name = form.account_name.trim();
      if (isBank) {
        payload.bank_account_name = form.bank_account_name.trim();
        payload.bank_account_number = form.bank_account_number.trim();
        payload.bank_ifsc = form.bank_ifsc.trim().toUpperCase();
        // For bank transfers the "address" field mirrors the account number for backend compatibility
        payload.address = form.bank_account_number.trim();
      }
      await api.post("/withdrawals", payload);
      setMsg("Withdrawal request received. Awaiting admin review.");
      setForm({ amount: "", address: "", network: form.network, account_name: "", bank_account_name: "", bank_account_number: "", bank_ifsc: "" });
      refresh();
    } catch (err) { setMsg(err.response?.data?.detail || "Could not submit withdrawal."); }
  };
  return (
    <div className="content-grid">
      <section className="panel wide action-panel">
        <div className="eyebrow">MANUAL REVIEW</div>
        <h2>Request a withdrawal</h2>
        <p className="panel-copy">Available balance: <b>{money(data.stats?.balance)}</b></p>

        {inCooldown && (
          <div className="error-box" data-testid="withdraw-cooldown" style={{display:"flex",gap:10,alignItems:"flex-start"}}>
            <Clock3 size={16} style={{flexShrink:0,marginTop:2}} />
            <div>
              <b>Withdrawal cooldown active.</b><br />
              You can submit the next request in <b data-testid="withdraw-cooldown-timer">{fmtCountdown(cooldownRemaining)}</b>. This was set after your previous withdrawal was rejected.
            </div>
          </div>
        )}

        <div className="network-toggle three" data-testid="withdraw-network-toggle">
          <button type="button" className={`toggle-btn ${form.network === "BEP20" ? "on" : ""}`} onClick={() => setNetwork("BEP20")} data-testid="network-bep20">
            <b>USDT · BEP20</b><small>BNB Smart Chain</small>
          </button>
          <button type="button" className={`toggle-btn ${isUpi ? "on" : ""}`} onClick={() => setNetwork("UPI")} data-testid="network-upi">
            <b>INR · UPI</b><small>{rate ? `1 USDT = ₹${rate.toFixed(2)}` : "Instant bank transfer"}</small>
          </button>
          <button type="button" className={`toggle-btn ${isBank ? "on" : ""}`} onClick={() => setNetwork("BANK")} data-testid="network-bank">
            <b>INR · Bank / Card</b><small>NEFT / IMPS to account</small>
          </button>
        </div>

        {isInr && rate && (
          <div className="rate-lock" data-testid="rate-lock-banner">
            <div><small>RATE</small><b>1 USDT = ₹{rate.toFixed(2)}</b></div>
            {form.amount && !isNaN(Number(form.amount)) && (
              <div><small>YOU'LL RECEIVE (INR)</small><b className="pos">₹{inrValue.toLocaleString(undefined, {maximumFractionDigits: 2})}</b></div>
            )}
            <div><small>SOURCE</small><b>Admin-set rate</b></div>
          </div>
        )}

        <p className="panel-copy small">
          {isBank
            ? "Payout will be sent via NEFT/IMPS to the bank account you provide. The INR rate shown is locked at submission."
            : isUpi
              ? "The INR rate shown will be locked when you submit; admin will use it for the payout."
              : "Payout sent on the BEP20 (BNB Smart Chain) network only."} {settings?.withdrawal_rules}
        </p>
        <form className="form-stack" onSubmit={submit}>
          <label>Amount USDT (min {money(settings?.min_withdrawal || 20)} · max {money(data.stats?.balance || 0)})
            <div style={{position:"relative"}}>
              <input data-testid="withdraw-amount-input" type="number" min="1" max={data.stats?.balance || undefined} step="0.01" required value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              <button type="button" className="max-btn" onClick={() => setForm({ ...form, amount: String(data.stats?.balance || 0) })} data-testid="withdraw-max-btn">MAX</button>
            </div>
            {form.amount && Number(form.amount) > (data.stats?.balance || 0) && (
              <span className="field-error" data-testid="withdraw-insufficient">Insufficient balance. Available: {money(data.stats?.balance || 0)}</span>
            )}
          </label>
          {!isBank && (
            <label>{isUpi ? "UPI ID" : "Destination BEP20 wallet address"}
              <input data-testid="withdraw-address-input" required value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder={isUpi ? "e.g. name@paytm / name@okhdfcbank" : "0x…"} />
            </label>
          )}
          {isUpi && (
            <label>Account holder name (as per UPI)
              <input data-testid="withdraw-upi-name" required value={form.account_name}
                onChange={(e) => setForm({ ...form, account_name: e.target.value })}
                placeholder="e.g. Ravi Kumar" />
            </label>
          )}
          {isBank && (
            <>
              <label>Account holder name (as per bank)
                <input data-testid="withdraw-bank-name" required value={form.bank_account_name}
                  onChange={(e) => setForm({ ...form, bank_account_name: e.target.value })}
                  placeholder="e.g. Ravi Kumar" />
              </label>
              <label>Bank account number
                <input data-testid="withdraw-bank-account" required value={form.bank_account_number}
                  onChange={(e) => setForm({ ...form, bank_account_number: e.target.value.replace(/\s/g, "") })}
                  placeholder="e.g. 123456789012" inputMode="numeric" />
              </label>
              <label>IFSC code
                <input data-testid="withdraw-bank-ifsc" required value={form.bank_ifsc}
                  onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value.toUpperCase() })}
                  placeholder="e.g. HDFC0001234" maxLength={11} />
              </label>
            </>
          )}
          <button data-testid="withdraw-submit-button" className="button primary" disabled={inCooldown || !form.amount || Number(form.amount) > (data.stats?.balance || 0)}>
            {inCooldown ? "Cooldown active" : "Submit for review"} <ArrowUpRight size={15} />
          </button>
        </form>
        {msg && <div className="success-box" data-testid="withdraw-message"><CheckCircle2 /> {msg}</div>}
        <p className="panel-copy small warn" style={{marginTop:16}}>
          <AlertCircle size={13} />
          {isBank
            ? "Verify the account number and IFSC carefully. Bank transfers to wrong accounts cannot be reversed."
            : isUpi
              ? "Only Indian UPI IDs are accepted. Double-check the ID — wrong IDs cannot be reversed."
              : "Provide a BEP20-compatible wallet address only. Non-BEP20 addresses will be rejected."}
        </p>
      </section>
      <section className="panel">
        <div className="eyebrow">HISTORY</div>
        <h2>Recent withdrawals</h2>
        {(data.withdrawals || []).length ? (
          <div className="activity-list">
            {data.withdrawals.slice(0, 6).map((x) => (
              <div className="activity-row" key={x.withdrawal_id} data-testid={`withdraw-history-${x.withdrawal_id}`}>
                <div style={{flex:1}}>
                  <b>{x.withdrawal_id}</b>
                  <small>{x.network} · {new Date(x.created_at).toLocaleDateString()}</small>
                  {x.status === "rejected" && x.admin_note && (
                    <div className="reject-reason" data-testid={`withdraw-reason-${x.withdrawal_id}`}>
                      <AlertCircle size={11} /> <span>Reason: {x.admin_note}</span>
                    </div>
                  )}
                </div>
                <strong>-{money(x.amount)}{x.locked_inr_amount ? ` · ₹${x.locked_inr_amount.toLocaleString()}` : ""}</strong>
                <span className={`status ${x.status}`}>{x.status}</span>
              </div>
            ))}
          </div>
        ) : <div className="empty-state"><b>No withdrawals yet</b></div>}
      </section>
    </div>
  );
}

function TransactionsPanel() {
  const [tx, setTx] = useState([]);
  useEffect(() => { api.get("/transactions").then((r) => setTx(r.data)).catch(() => {}); }, []);
  return (
    <section className="panel wide">
      <div className="eyebrow">LEDGER</div>
      <h2>Transaction history</h2>
      {tx.length ? (
        <table className="data-table">
          <thead><tr><th>ID</th><th>Kind</th><th>Amount</th><th>Description</th><th>Date</th></tr></thead>
          <tbody>
            {tx.map((x) => (
              <tr key={x.tx_id} data-testid={`tx-row-${x.tx_id}`}>
                <td>{x.tx_id}</td>
                <td><span className={`tag tag-${x.kind}`}>{x.kind}</span></td>
                <td className={x.amount >= 0 ? "pos" : "neg"}>{x.amount >= 0 ? "+" : ""}{money(x.amount)}</td>
                <td>{x.description}</td>
                <td>{new Date(x.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div className="empty-state"><b>No transactions yet</b></div>}
    </section>
  );
}

function ReferralPoster({ code, link, name }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const w = 1080, h = 1080;
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, "#0d2942");
    grad.addColorStop(1, "#164876");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    // Decorative circle
    ctx.beginPath();
    ctx.arc(w - 80, h - 80, 340, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(126,200,227,0.10)";
    ctx.fill();
    // Brand mark
    ctx.fillStyle = "#7ec8e3";
    ctx.fillRect(72, 72, 74, 74);
    ctx.fillStyle = "#0d2942";
    ctx.font = "bold 46px Georgia, serif";
    ctx.textBaseline = "middle";
    ctx.fillText("N", 96, 110);
    // Brand text
    ctx.fillStyle = "#fff";
    ctx.font = "bold 30px Georgia, serif";
    ctx.fillText("Nexa Pro", 170, 110);
    // Eyebrow
    ctx.fillStyle = "#7ec8e3";
    ctx.font = "600 20px 'DM Mono', monospace";
    ctx.fillText("REFER · EARN · REPEAT", 72, 250);
    // Big headline
    ctx.fillStyle = "#fff";
    ctx.font = "700 92px Georgia, serif";
    ctx.fillText("Join me on", 72, 340);
    ctx.font = "italic 700 92px Georgia, serif";
    ctx.fillStyle = "#7ec8e3";
    ctx.fillText("Nexa Pro", 72, 440);
    // Sub
    ctx.fillStyle = "#c8d8e6";
    ctx.font = "400 30px system-ui, sans-serif";
    if (name) ctx.fillText(`Invited by ${name}`, 72, 500);
    ctx.fillText("Private USDT staking with daily earnings.", 72, name ? 545 : 505);
    // Code
    ctx.fillStyle = "#8ea3b8";
    ctx.font = "500 22px 'DM Mono', monospace";
    ctx.fillText("YOUR REFERRAL CODE", 72, 660);
    ctx.fillStyle = "#7ec8e3";
    ctx.font = "700 68px 'DM Mono', monospace";
    ctx.fillText(code, 72, 730);
    // QR
    try {
      const dataUrl = await QRCode.toDataURL(link, { margin: 1, width: 320, color: { dark: "#0d2942", light: "#ffffff" } });
      const img = new Image();
      img.onload = () => {
        ctx.fillStyle = "#fff";
        ctx.fillRect(680, 640, 340, 340);
        ctx.drawImage(img, 690, 650, 320, 320);
        // Footer link
        ctx.fillStyle = "#c8d8e6";
        ctx.font = "500 22px 'DM Mono', monospace";
        ctx.fillText("SCAN OR VISIT", 72, 900);
        ctx.fillStyle = "#fff";
        ctx.font = "500 24px 'DM Mono', monospace";
        const shortLink = link.replace(/^https?:\/\//, "");
        ctx.fillText(shortLink, 72, 950);
        // Disclaimer
        ctx.fillStyle = "#8ea3b8";
        ctx.font = "400 18px system-ui, sans-serif";
        ctx.fillText("Digital-asset activity involves risk. Target returns are not guaranteed.", 72, 1010);
        setReady(true);
      };
      img.src = dataUrl;
    } catch (e) { setReady(true); }
  }, [code, link, name]);
  useEffect(() => { draw(); }, [draw]);

  const download = () => {
    if (!canvasRef.current) return;
    canvasRef.current.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `nexa-pro-referral-${code}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const nativeShare = async () => {
    if (!canvasRef.current || !navigator.share) return download();
    canvasRef.current.toBlob(async (blob) => {
      const file = new File([blob], `nexa-pro-${code}.png`, { type: "image/png" });
      try {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "Join me on Nexa Pro",
            text: `Use my referral code ${code} to sign up on Nexa Pro.`,
          });
        } else {
          download();
        }
      } catch (e) { /* user cancelled */ }
    }, "image/png");
  };

  return (
    <div className="poster-wrap">
      <canvas ref={canvasRef} className="poster-canvas" data-testid="referral-poster-canvas" />
      <div className="poster-actions">
        <button onClick={download} className="button primary" data-testid="poster-download" disabled={!ready}>
          Download poster
        </button>
        <button onClick={nativeShare} className="button ghost" data-testid="poster-share" disabled={!ready}>
          Share…
        </button>
      </div>
    </div>
  );
}

function ReferralPanel() {
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPoster, setShowPoster] = useState(false);
  useEffect(() => {
    api.get("/referrals").then((r) => setData(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="loading">Loading referrals…</div>;
  const link = `${window.location.origin}/login?ref=${data.referral_code}`;
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1600); };
  const waMsg = `Join me on Nexa Pro — a private USDT staking platform. Use my referral code *${data.referral_code}* when you sign up:\n${link}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(waMsg)}`;
  return (
    <div className="content-grid stack">
      <section className="panel wide">
        <div className="eyebrow">REFER & EARN</div>
        <h2>Grow with your network</h2>
        <p className="panel-copy">Earn {data.percent}% direct on invitees' first deposits, plus {data.l2_percent ?? 1}% from their invitees' first deposits.</p>
        {data.has_active_plan === false ? (
          <div className="error-box" data-testid="referral-plan-warning" style={{display:"flex",gap:10,alignItems:"flex-start"}}>
            <AlertCircle size={16} style={{flexShrink:0,marginTop:2}} />
            <div>
              <b>Referral rewards are queued.</b><br />
              Your referral earnings will show as <b>Queued</b> until you start any investment plan. The moment you activate a plan, all queued rewards are released and credited to your balance automatically.
              {data.pending_rewards > 0 && (
                <div style={{marginTop:8}}><b>{money(data.pending_rewards)}</b> is currently waiting to be credited.</div>
              )}
            </div>
          </div>
        ) : (
          <div className="success-box" data-testid="referral-plan-ok" style={{display:"flex",gap:10,alignItems:"center"}}>
            <CheckCircle2 size={16} /> Referral rewards are active — any queued earnings have been credited to your balance.
          </div>
        )}
        <div className="referral-box">
          <div><small>YOUR REFERRAL CODE</small><b data-testid="referral-code">{data.referral_code}</b></div>
          <div className="wide"><small>SHARE LINK</small>
            <b data-testid="referral-link" className="mono">{link}</b>
            <div className="button-row" style={{marginTop:10}}>
              <button className="mini-button" onClick={copy} data-testid="copy-referral-button">
                <Copy size={13} /> {copied ? "Copied" : "Copy link"}
              </button>
              <a className="mini-button wa-button" href={waHref} target="_blank" rel="noreferrer" data-testid="whatsapp-share-button">
                <svg width="13" height="13" viewBox="0 0 32 32" fill="currentColor" aria-hidden><path d="M16 3C9.4 3 4 8.4 4 15c0 2.5.8 4.8 2.1 6.8L4 29l7.4-2c1.9 1 4.1 1.6 6.6 1.6 6.6 0 12-5.4 12-12S22.6 3 16 3zm0 22c-2.1 0-4.1-.6-5.8-1.7l-.4-.2-4.4 1.2 1.2-4.3-.3-.4C5.4 18.1 4.8 16.5 4.8 15c0-6.2 5-11.2 11.2-11.2S27.2 8.8 27.2 15 22.2 25 16 25zm6.2-8.4c-.3-.2-2-1-2.3-1.1-.3-.1-.5-.2-.8.1-.2.3-.9 1.1-1.1 1.3-.2.2-.4.3-.7.1-.3-.2-1.4-.5-2.6-1.6-1-.9-1.6-2-1.8-2.3-.2-.3 0-.5.1-.6.1-.2.3-.4.4-.6.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5s-.7-1.7-1-2.3c-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.2.2 2.2 3.3 5.3 4.6.7.3 1.3.5 1.8.6.7.2 1.4.2 1.9.1.6-.1 2-.8 2.3-1.6.3-.8.3-1.4.2-1.6-.1-.1-.3-.2-.6-.3z"/></svg>
                WhatsApp
              </a>
              <button className="mini-button" onClick={() => setShowPoster(!showPoster)} data-testid="toggle-poster-button">
                {showPoster ? "Hide poster" : "Generate poster"}
              </button>
            </div>
          </div>
        </div>
        <div className="metric-grid four">
          <Metric icon={<Users />} label="Total referrals" value={data.total_referrals} />
          <Metric icon={<Activity />} label="Active" value={data.active_referrals} />
          <Metric icon={<Wallet />} label="L1 / L2" value={`${data.percent}% / ${data.l2_percent ?? 1}%`} />
          <Metric icon={<TrendingUp />} label="Paid earnings" value={money(data.total_rewards)} sub={data.pending_rewards > 0 ? `${money(data.pending_rewards)} queued` : "No pending"} />
        </div>
        {showPoster && (
          <div style={{ marginTop: 22 }}>
            <div className="eyebrow">SHAREABLE POSTER</div>
            <h3 style={{fontSize:20,margin:"6px 0 12px"}}>Perfect for Instagram / WhatsApp status</h3>
            <ReferralPoster code={data.referral_code} link={link} name={me?.name} />
          </div>
        )}
      </section>
      <section className="panel wide">
        <div className="eyebrow">REWARD HISTORY</div>
        <h2>Recent rewards</h2>
        {data.rewards.length ? (
          <table className="data-table">
            <thead><tr><th>Reward</th><th>Referred user</th><th>Tier</th><th>Percent</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>
              {data.rewards.map((r) => (
                <tr key={r.reward_id} data-testid={`referral-reward-${r.reward_id}`}>
                  <td>{r.reward_id}</td>
                  <td>{r.referred_user_id}</td>
                  <td>L{r.level ?? 1}</td>
                  <td>{r.percent}%</td>
                  <td className={r.status === "pending" ? "" : "pos"}>{money(r.amount)}</td>
                  <td><span className={`status ${r.status === "pending" ? "pending" : "active"}`}>{r.status === "pending" ? "Queued" : "Paid"}</span></td>
                  <td>{new Date(r.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="empty-state"><b>No rewards yet</b><span>Share your link to invite people to Nexa Pro</span></div>}
      </section>
    </div>
  );
}

function ProfilePanel({ user }) {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  const canApplyAgent = settings?.agent_apply_enabled && settings?.telegram_link;
  return (
    <section className="panel wide">
      <div className="eyebrow">PROFILE</div>
      <h2>Account details</h2>
      <div className="profile-grid">
        <div><small>USER ID</small><b data-testid="profile-user-id">{user?.user_id}</b></div>
        <div><small>NAME</small><b>{user?.name}</b></div>
        <div><small>EMAIL</small><b>{user?.email}</b></div>
        <div><small>STATUS</small><b><span className={`status ${user?.status}`}>{user?.status}</span></b></div>
        <div><small>REFERRAL CODE</small><b>{user?.referral_code}</b></div>
        <div><small>MEMBER SINCE</small><b>{fmtDT(user?.created_at)}</b></div>
      </div>
      {canApplyAgent && (
        <div className="agent-apply-row" data-testid="agent-apply-section">
          <div>
            <div className="eyebrow mint" style={{color:"#2f8fb8"}}>EARN MORE</div>
            <h3 style={{fontSize:20,letterSpacing:"-.04em",margin:"6px 0 4px"}}>Become a Nexa Pro Agent</h3>
            <p className="panel-copy small" style={{maxWidth:460}}>Agents help onboard investors, earn higher commissions and get priority support. Chat with our team on Telegram to apply.</p>
          </div>
          <a href={normalizeTelegramLink(settings.telegram_link)} target="_blank" rel="noreferrer noopener" className="button primary" data-testid="apply-agent-btn">
            <ArrowUpRight size={14} /> Apply on Telegram
          </a>
        </div>
      )}
    </section>
  );
}

// ---------------- Admin Area ----------------
function AdminArea({ tab, data, refresh }) {
  if (tab === "Overview") return <AdminQueue data={data} refresh={refresh} />;
  if (tab === "Users") return <AdminUsers />;
  if (tab === "Deposits") return <AdminDeposits refresh={refresh} />;
  if (tab === "Withdrawals") return <AdminWithdrawals refresh={refresh} />;
  if (tab === "Investments") return <AdminInvestments />;
  if (tab === "Plans") return <AdminPlans />;
  if (tab === "News") return <AdminNews />;
  if (tab === "Referrals") return <AdminReferrals />;
  if (tab === "Support") return <AdminSupport />;
  if (tab === "Announce") return <AdminBroadcast />;
  if (tab === "Admins") return <AdminAdmins />;
  if (tab === "Settings") return <AdminSettings />;
  if (tab === "Access") return <AdminAccess />;
  if (tab === "Audit") return <AdminAudit />;
  return null;
}

function AdminQueue({ data, refresh }) {
  const act = async (kind, id, status, note = "Reviewed", tx_hash = "") => {
    await api.post(`/admin/${kind}/${id}/status`, { status, note, tx_hash });
    refresh();
  };
  return (
    <div className="content-grid">
      <section className="panel wide">
        <div className="panel-head">
          <div><div className="eyebrow">MANUAL QUEUE</div><h2>Deposit reviews</h2></div>
          <span className="count-badge">{data.pending_deposits} pending</span>
        </div>
        <div className="activity-list">
          {data.deposits?.length ? data.deposits.slice(0, 10).map((x) => (
            <div className="activity-row" key={x.deposit_id}>
              <div><b>{x.deposit_id}</b><small>{x.user_id} · {x.network} · Tx {short(x.txid)}…</small></div>
              <strong>{money(x.amount)}</strong>
              <span className={`status ${x.status}`}>{x.status}</span>
              {x.status === "pending" && (
                <>
                  <button data-testid={`approve-${x.deposit_id}`} onClick={() => act("deposits", x.deposit_id, "approved")} className="mini-button success">Approve</button>
                  <button data-testid={`reject-${x.deposit_id}`} onClick={() => act("deposits", x.deposit_id, "rejected", "Not verified")} className="mini-button danger">Reject</button>
                </>
              )}
            </div>
          )) : <div className="empty-state"><b>Queue is clear</b></div>}
        </div>
      </section>
      <section className="panel wide">
        <div className="eyebrow">WITHDRAWAL QUEUE</div>
        <h2>Process requests</h2>
        <div className="activity-list">
          {data.withdrawals?.length ? data.withdrawals.slice(0, 10).map((x) => (
            <div className="activity-row" key={x.withdrawal_id}>
              <div><b>{x.withdrawal_id}</b><small>{x.user_id} · {x.network}</small></div>
              <strong>{money(x.amount)}</strong>
              <span className={`status ${x.status}`}>{x.status}</span>
              {x.status === "pending" && (
                <button data-testid={`process-${x.withdrawal_id}`} onClick={() => act("withdrawals", x.withdrawal_id, "processing")} className="mini-button">Process</button>
              )}
              {x.status === "processing" && (
                <button data-testid={`complete-${x.withdrawal_id}`}
                  onClick={() => {
                    const h = prompt("Enter tx hash for this payout"); if (h) act("withdrawals", x.withdrawal_id, "completed", "Paid", h);
                  }} className="mini-button success">Complete</button>
              )}
            </div>
          )) : <div className="empty-state"><b>Queue is clear</b></div>}
        </div>
      </section>
    </div>
  );
}

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [adjustUser, setAdjustUser] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ amount: "", reason: "" });
  const [detailUserId, setDetailUserId] = useState(null);
  const [msgUser, setMsgUser] = useState(null);
  const [msg, setMsg] = useState("");
  const load = useCallback(async () => {
    const r = await api.get(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`); setUsers(r.data);
  }, [q]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (u) => {
    await api.post(`/admin/users/${u.user_id}/status`, { status: u.status === "active" ? "blocked" : "active" });
    load();
  };
  const submitAdjust = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post(`/admin/users/${adjustUser.user_id}/adjust-balance`, {
        amount: Number(adjustForm.amount), reason: adjustForm.reason,
      });
      setMsg(`Balance updated. New: $${r.data.new_balance}`);
      setAdjustUser(null); setAdjustForm({ amount: "", reason: "" }); load();
      setTimeout(() => setMsg(""), 2500);
    } catch (err) { setMsg(err.response?.data?.detail || "Adjustment failed"); }
  };
  return (
    <section className="panel wide">
      <div className="panel-head">
        <div><div className="eyebrow">USER MANAGEMENT</div><h2>All investors</h2></div>
        <input className="search-input" placeholder="Search by name, email, ID" value={q}
          onChange={(e) => setQ(e.target.value)} data-testid="admin-user-search" />
      </div>
      {msg && <div className="success-box" data-testid="admin-user-message"><CheckCircle2 /> {msg}</div>}
      <table className="data-table">
        <thead><tr><th>User ID</th><th>Name</th><th>Email</th><th>Balance</th><th>Status</th><th>Joined</th><th /></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
              <td><button type="button" className="link-inline" onClick={() => setDetailUserId(u.user_id)} data-testid={`user-open-${u.user_id}`}>{u.user_id}</button></td>
              <td>{u.name}</td><td>{u.email}</td>
              <td>{money(u.balance)}</td>
              <td><span className={`status ${u.status}`}>{u.status}</span></td>
              <td>{fmtDT(u.created_at)}</td>
              <td>
                <button className="mini-button" onClick={() => setDetailUserId(u.user_id)} data-testid={`user-details-${u.user_id}`}>View details</button>
                <button className="mini-button" onClick={() => setMsgUser(u)} data-testid={`user-message-${u.user_id}`}>Message</button>
                <button className="mini-button" onClick={() => setAdjustUser(u)} data-testid={`user-adjust-${u.user_id}`}>Adjust balance</button>
                <button className={`mini-button ${u.status === "active" ? "danger" : "success"}`}
                  onClick={() => toggle(u)} data-testid={`user-toggle-${u.user_id}`}>
                  {u.status === "active" ? "Block" : "Unblock"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!users.length && <div className="empty-state"><b>No users found</b></div>}
      {adjustUser && (
        <div className="modal-backdrop" onClick={() => setAdjustUser(null)}>
          <form className="panel modal" onClick={(e) => e.stopPropagation()} onSubmit={submitAdjust}>
            <div className="eyebrow">ADJUST BALANCE</div>
            <h3>{adjustUser.name} · {adjustUser.user_id}</h3>
            <p className="panel-copy small">Current balance: <b>{money(adjustUser.balance)}</b>. Use positive amount to credit, negative to debit. Every adjustment is logged in the audit trail.</p>
            <label>Amount (USDT)
              <input type="number" step="0.01" required value={adjustForm.amount}
                onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })}
                data-testid="adjust-amount-input" />
            </label>
            <label>Reason
              <input required value={adjustForm.reason}
                onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                data-testid="adjust-reason-input" placeholder="e.g. Promo bonus / Refund / Correction" />
            </label>
            <div className="button-row">
              <button className="button primary" data-testid="adjust-submit-button">Apply adjustment</button>
              <button type="button" className="button ghost" onClick={() => setAdjustUser(null)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {detailUserId && <UserDetailModal userId={detailUserId} onClose={() => setDetailUserId(null)} onMessage={(id) => { setDetailUserId(null); setMsgUser({ user_id: id }); }} />}
      {msgUser && <AdminMessageModal user={msgUser} onClose={() => setMsgUser(null)} onSent={() => { setMsg(`Message sent to ${msgUser.user_id}`); setTimeout(() => setMsg(""), 2500); }} />}
    </section>
  );
}

function AdminMessageModal({ user, onClose, onSent }) {
  const [text, setText] = useState("");
  const [thread, setThread] = useState([]);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/support/${user.user_id}`);
      setThread(Array.isArray(r.data) ? r.data : r.data.messages || []);
    } catch (e) { /* no thread yet is fine */ }
  }, [user.user_id]);
  useEffect(() => { load(); }, [load]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true); setErr("");
    try {
      await api.post("/admin/support", { user_id: user.user_id, message: text.trim() });
      setText(""); load(); onSent && onSent();
    } catch (e) { setErr(e.response?.data?.detail || "Could not send message"); }
    finally { setSending(false); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="admin-message-modal">
      <div className="panel admin-message-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="admin-message-close"><X size={18} /></button>
        <div className="eyebrow">SEND MESSAGE</div>
        <h3 style={{fontSize:22,letterSpacing:"-.04em",margin:"6px 0 6px"}}>Message {user.name || user.user_id}</h3>
        <p className="panel-copy small">User receives this inside their <b>Help & support</b> tab and gets a notification instantly.</p>
        <div className="chat-thread" data-testid="admin-message-thread">
          {thread.length ? thread.slice(-20).map((m) => (
            <div key={m.message_id} className={`chat-bubble ${m.sender === "admin" ? "admin" : "user"}`}>
              <small>{m.sender === "admin" ? "You" : "User"} · {fmtDT(m.created_at)}</small>
              <span>{m.message}</span>
            </div>
          )) : <div className="chat-empty">No prior messages. Start the conversation below.</div>}
        </div>
        {err && <div className="error-box" data-testid="admin-message-error">{err}</div>}
        <form onSubmit={send} className="chat-compose">
          <textarea rows={3} required value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Type a message for this user…" data-testid="admin-message-input" />
          <div className="button-row">
            <button type="submit" className="button primary" disabled={sending} data-testid="admin-message-send">
              {sending ? "Sending…" : "Send message"} <ArrowUpRight size={14} />
            </button>
            <button type="button" className="button ghost" onClick={onClose}>Close</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserDetailModal({ userId, onClose, onMessage }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/admin/users/${userId}`).then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || "Failed to load"));
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [userId, onClose]);
  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="user-detail-modal">
      <div className="panel user-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="user-detail-close"><X size={18} /></button>
        {err && <div className="error-box"><AlertCircle size={14} /> {err}</div>}
        {!data ? <div className="loading" style={{minHeight:200}}>Loading user…</div> : (
          <>
            <div className="eyebrow">USER PROFILE</div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",gap:12,flexWrap:"wrap"}}>
              <h3 style={{margin:"6px 0 4px",fontSize:24,letterSpacing:"-.04em"}}>{data.user.name} <span className="muted-dash">·</span> {data.user.user_id}</h3>
              {onMessage && <button type="button" className="button primary" onClick={() => onMessage(data.user.user_id)} data-testid="user-detail-message-btn"><MessageCircle size={14} /> Message user</button>}
            </div>
            <p className="panel-copy small">{data.user.email} · referral code <b>{data.user.referral_code}</b>{data.user.referred_by ? <> · referred by <b>{data.user.referred_by}</b></> : null} · joined {fmtDT(data.user.created_at)}</p>
            {data.referrer && (
              <div className="referrer-card" data-testid="user-detail-referrer">
                <div className="eyebrow mint" style={{color:"#2f8fb8"}}>REFERRED BY</div>
                <div style={{display:"flex",gap:14,alignItems:"center",flexWrap:"wrap",marginTop:4}}>
                  <b style={{fontSize:16}}>{data.referrer.name}</b>
                  <span className="muted-dash">·</span>
                  <span>{data.referrer.user_id}</span>
                  <span className="muted-dash">·</span>
                  <span>{data.referrer.email}</span>
                  <span className="muted-dash">·</span>
                  <span>Code <b>{data.referrer.referral_code}</b></span>
                </div>
              </div>
            )}
            <div className="metric-grid four" style={{marginTop:16}}>
              <Metric icon={<Wallet />} label="Available balance" value={money(data.user.balance)} />
              <Metric icon={<ArrowUpRight />} label="Total deposited" value={money(data.summary.total_deposited)} sub={`${data.summary.deposit_count} approved · ${money(data.summary.pending_deposited)} pending`} />
              <Metric icon={<ArrowUpRight />} label="Total withdrawn" value={money(data.summary.total_withdrawn)} sub={`${data.summary.withdrawal_count} done · ${money(data.summary.pending_withdrawn)} in review`} />
              <Metric icon={<TrendingUp />} label="Active invested" value={money(data.summary.total_invested)} sub={`${data.summary.active_investments} plans active`} />
            </div>
            <div className="metric-grid four" style={{marginTop:10}}>
              <Metric icon={<Users />} label="Total referrals" value={data.summary.total_referrals} sub={`${data.summary.active_referrals} active · ${data.summary.depositing_referrals} deposited`} />
              <Metric icon={<Wallet />} label="Paid referral bonus" value={money(data.summary.paid_referral_bonus)} />
              <Metric icon={<Clock3 />} label="Queued referral bonus" value={money(data.summary.pending_referral_bonus)} />
              <Metric icon={<Activity />} label="Account status" value={data.user.status} />
            </div>

            <div className="eyebrow" style={{marginTop:22}}>INVESTMENT PLANS</div>
            {data.investments.length ? (
              <table className="data-table">
                <thead><tr><th>Investment ID</th><th>Plan</th><th>Amount</th><th>Rate</th><th>Duration</th><th>Status</th><th>Started</th></tr></thead>
                <tbody>
                  {data.investments.map((i) => (
                    <tr key={i.investment_id} data-testid={`detail-invest-${i.investment_id}`}>
                      <td>{i.investment_id}</td>
                      <td>{i.plan_name}</td>
                      <td>{money(i.amount)}</td>
                      <td>{i.daily_rate_percent}% / day</td>
                      <td>{i.duration_days}d</td>
                      <td><span className={`status ${i.status}`}>{i.status}</span></td>
                      <td>{fmtDT(i.start_date || i.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="empty-state"><b>No investments yet</b></div>}

            <div className="eyebrow" style={{marginTop:22}}>INVITED USERS</div>
            {data.invited_users.length ? (
              <table className="data-table">
                <thead><tr><th>User ID</th><th>Name</th><th>Email</th><th>Status</th><th>Deposited</th><th>Joined</th></tr></thead>
                <tbody>
                  {data.invited_users.map((u) => (
                    <tr key={u.user_id} data-testid={`invited-row-${u.user_id}`}>
                      <td>{u.user_id}</td>
                      <td>{u.name}</td>
                      <td>{u.email}</td>
                      <td><span className={`status ${u.status}`}>{u.status}</span></td>
                      <td>{money(u.total_deposited)} <small style={{color:"#8ea3b8"}}>({u.deposit_count})</small></td>
                      <td>{fmtDT(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="empty-state"><b>No invited users yet</b></div>}

            <div className="eyebrow" style={{marginTop:22}}>REFERRAL REWARDS</div>
            {data.referral_rewards.length ? (
              <table className="data-table">
                <thead><tr><th>Reward</th><th>From user</th><th>Tier</th><th>Percent</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
                <tbody>
                  {data.referral_rewards.map((r) => (
                    <tr key={r.reward_id}>
                      <td>{r.reward_id}</td>
                      <td>{r.referred_user_id}</td>
                      <td>L{r.level ?? 1}</td>
                      <td>{r.percent}%</td>
                      <td className={r.status === "pending" ? "" : "pos"}>{money(r.amount)}</td>
                      <td><span className={`status ${r.status === "pending" ? "pending" : "active"}`}>{r.status === "pending" ? "Queued" : "Paid"}</span></td>
                      <td>{fmtDT(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="empty-state"><b>No referral rewards yet</b></div>}

            <div className="eyebrow" style={{marginTop:22}}>DEPOSITS</div>
            {data.deposits.length ? (
              <table className="data-table">
                <thead><tr><th>ID</th><th>Amount</th><th>Network</th><th>TxID</th><th>Status</th><th>Date</th></tr></thead>
                <tbody>
                  {data.deposits.map((d) => (
                    <tr key={d.deposit_id}>
                      <td>{d.deposit_id}</td><td>{money(d.amount)}</td><td>{d.network}</td>
                      <td className="mono wrap"><CopyField value={d.txid} /></td>
                      <td><span className={`status ${d.status}`}>{d.status}</span></td>
                      <td>{fmtDT(d.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="empty-state"><b>No deposits</b></div>}

            <div className="eyebrow" style={{marginTop:22}}>WITHDRAWALS</div>
            {data.withdrawals.length ? (
              <table className="data-table">
                <thead><tr><th>ID</th><th>Amount</th><th>Destination</th><th>Network</th><th>Status</th><th>Date</th></tr></thead>
                <tbody>
                  {data.withdrawals.map((w) => (
                    <tr key={w.withdrawal_id}>
                      <td>{w.withdrawal_id}</td><td>{money(w.amount)}</td>
                      <td className="mono wrap">
                        {w.network === "BANK" ? (
                          <div className="bank-stack">
                            <CopyField value={w.bank_account_name} label="Name" />
                            <CopyField value={w.bank_account_number} label="A/C" />
                            <CopyField value={w.bank_ifsc} label="IFSC" />
                          </div>
                        ) : <CopyField value={w.address} />}
                      </td>
                      <td>{w.network}</td>
                      <td><span className={`status ${w.status}`}>{w.status}</span></td>
                      <td>{fmtDT(w.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="empty-state"><b>No withdrawals</b></div>}
          </>
        )}
      </div>
    </div>
  );
}

function AdminDeposits({ refresh }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [picked, setPicked] = useState(new Set());
  const load = useCallback(() => api.get(`/admin/deposits${status ? `?status=${status}` : ""}`).then((r) => setItems(r.data)), [status]);
  useEffect(() => { load(); setPicked(new Set()); }, [load]);
  const act = async (id, s) => {
    const note = s === "rejected" ? prompt("Reason for rejection", "Not verified") : "Approved after verification";
    if (s === "rejected" && !note) return;
    await api.post(`/admin/deposits/${id}/status`, { status: s, note });
    load(); refresh();
  };
  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setPicked((p) => p.size === items.length ? new Set() : new Set(items.map((x) => x.deposit_id)));
  const delOne = async (id) => {
    if (!window.confirm("Delete this deposit record permanently?")) return;
    try { await api.delete(`/admin/deposits/${id}`); load(); refresh(); }
    catch (e) { window.alert(e.response?.data?.detail || "Delete failed"); }
  };
  const delBulk = async () => {
    if (!picked.size || !window.confirm(`Delete ${picked.size} deposit record(s) permanently?`)) return;
    try { await api.post("/admin/deposits/bulk-delete", { ids: Array.from(picked) }); setPicked(new Set()); load(); refresh(); }
    catch (e) { window.alert(e.response?.data?.detail || "Bulk delete failed"); }
  };
  return (
    <section className="panel wide">
      <div className="panel-head">
        <div><div className="eyebrow">DEPOSITS</div><h2>All deposits</h2></div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {picked.size > 0 && <button className="mini-button danger" onClick={delBulk} data-testid="dep-bulk-delete">Delete selected ({picked.size})</button>}
          <select className="search-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-deposit-filter">
            <option value="">All</option><option>pending</option><option>approved</option><option>rejected</option>
          </select>
        </div>
      </div>
      <table className="data-table">
        <thead><tr>
          <th><input type="checkbox" onChange={toggleAll} checked={!!items.length && picked.size === items.length} data-testid="dep-select-all" /></th>
          <th>ID</th><th>User</th><th>Amount</th><th>Network</th><th>TxID</th><th>Status</th><th>Time</th><th /></tr></thead>
        <tbody>
          {items.map((x) => (
            <tr key={x.deposit_id} data-testid={`admin-dep-${x.deposit_id}`}>
              <td><input type="checkbox" checked={picked.has(x.deposit_id)} onChange={() => toggle(x.deposit_id)} disabled={x.status === "pending"} data-testid={`dep-pick-${x.deposit_id}`} /></td>
              <td>{x.deposit_id}</td><td>{x.user_id}</td><td>{money(x.amount)}</td>
              <td>{x.network}</td><td className="mono wrap"><CopyField value={x.txid} testid={`dep-txid-${x.deposit_id}`} /></td>
              <td><span className={`status ${x.status}`}>{x.status}</span></td>
              <td style={{whiteSpace:"nowrap"}}>{fmtDT(x.created_at)}</td>
              <td>
                {x.status === "pending" ? (
                  <>
                    <button className="mini-button success" onClick={() => act(x.deposit_id, "approved")} data-testid={`dep-approve-${x.deposit_id}`}>Approve</button>
                    <button className="mini-button danger" onClick={() => act(x.deposit_id, "rejected")} data-testid={`dep-reject-${x.deposit_id}`}>Reject</button>
                  </>
                ) : (
                  <button className="mini-button danger" onClick={() => delOne(x.deposit_id)} data-testid={`dep-delete-${x.deposit_id}`}>Delete</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!items.length && <div className="empty-state"><b>No deposits</b></div>}
    </section>
  );
}

function AdminWithdrawals({ refresh }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get(`/admin/withdrawals${status ? `?status=${status}` : ""}`).then((r) => setItems(r.data)), [status]);
  useEffect(() => { load(); setPicked(new Set()); }, [load]);
  const act = async (id, s) => {
    let tx = ""; let note = `Marked ${s}`; let cooldown_hours = null;
    if (s === "completed") { tx = prompt("Enter payout tx hash") || ""; if (!tx) return; }
    if (s === "rejected") {
      note = prompt("Reason for rejection (visible to user)", "Not eligible") || "";
      if (!note) return;
      const cdRaw = prompt("Cooldown before user can withdraw again (in hours). Leave blank or 0 for none.", "24");
      if (cdRaw === null) return;
      const cdNum = parseFloat(cdRaw);
      cooldown_hours = !isNaN(cdNum) && cdNum > 0 ? cdNum : null;
    }
    const payload = { status: s, note, tx_hash: tx };
    if (cooldown_hours != null) payload.cooldown_hours = cooldown_hours;
    await api.post(`/admin/withdrawals/${id}/status`, payload);
    load(); refresh();
  };
  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setPicked((p) => p.size === items.length ? new Set() : new Set(items.map((x) => x.withdrawal_id)));
  const delOne = async (id) => {
    if (!window.confirm("Delete this withdrawal record permanently?")) return;
    try { await api.delete(`/admin/withdrawals/${id}`); load(); refresh(); }
    catch (e) { window.alert(e.response?.data?.detail || "Delete failed"); }
  };
  const delBulk = async () => {
    if (!picked.size || !window.confirm(`Delete ${picked.size} withdrawal record(s) permanently?`)) return;
    try { await api.post("/admin/withdrawals/bulk-delete", { ids: Array.from(picked) }); setPicked(new Set()); load(); refresh(); }
    catch (e) { window.alert(e.response?.data?.detail || "Bulk delete failed"); }
  };
  const canDelete = (s) => !["pending", "processing"].includes(s);
  return (
    <section className="panel wide">
      <div className="panel-head">
        <div><div className="eyebrow">WITHDRAWALS</div><h2>Requests</h2></div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {picked.size > 0 && <button className="mini-button danger" onClick={delBulk} data-testid="wd-bulk-delete">Delete selected ({picked.size})</button>}
          <select className="search-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-withdrawal-filter">
            <option value="">All</option><option>pending</option><option>processing</option><option>completed</option><option>rejected</option>
          </select>
        </div>
      </div>
      <table className="data-table">
        <thead><tr>
          <th><input type="checkbox" onChange={toggleAll} checked={!!items.length && picked.size === items.length} data-testid="wd-select-all" /></th>
          <th>ID</th><th>User</th><th>Amount</th><th>Address</th><th>Network</th><th>Status</th><th>Time</th><th /></tr></thead>
        <tbody>
          {items.map((x) => (
            <tr key={x.withdrawal_id} data-testid={`admin-wd-${x.withdrawal_id}`}>
              <td><input type="checkbox" checked={picked.has(x.withdrawal_id)} onChange={() => toggle(x.withdrawal_id)} disabled={!canDelete(x.status)} data-testid={`wd-pick-${x.withdrawal_id}`} /></td>
              <td>{x.withdrawal_id}</td><td>{x.user_id}</td><td>{money(x.amount)}</td>
              <td className="mono wrap">
                {x.network === "BANK" ? (
                  <div className="bank-stack">
                    <CopyField value={x.bank_account_name} label="Name" testid={`wd-bank-name-${x.withdrawal_id}`} />
                    <CopyField value={x.bank_account_number} label="A/C" testid={`wd-bank-acct-${x.withdrawal_id}`} />
                    <CopyField value={x.bank_ifsc} label="IFSC" testid={`wd-bank-ifsc-${x.withdrawal_id}`} />
                  </div>
                ) : x.network === "UPI" ? (
                  <div className="bank-stack">
                    <CopyField value={x.account_name} label="Name" testid={`wd-upi-name-${x.withdrawal_id}`} />
                    <CopyField value={x.address} label="UPI ID" testid={`wd-upi-id-${x.withdrawal_id}`} />
                  </div>
                ) : (
                  <CopyField value={x.address} testid={`wd-addr-${x.withdrawal_id}`} />
                )}
              </td><td>{x.network}</td>
              <td><span className={`status ${x.status}`}>{x.status}</span></td>
              <td style={{whiteSpace:"nowrap"}}>{fmtDT(x.created_at)}</td>
              <td>
                {x.status === "pending" && (<>
                  <button className="mini-button" onClick={() => setEditing(x)} data-testid={`wd-edit-${x.withdrawal_id}`}>Edit</button>
                  <button className="mini-button" onClick={() => act(x.withdrawal_id, "processing")} data-testid={`wd-process-${x.withdrawal_id}`}>Process</button>
                  <button className="mini-button danger" onClick={() => act(x.withdrawal_id, "rejected")} data-testid={`wd-reject-${x.withdrawal_id}`}>Reject</button>
                </>)}
                {x.status === "processing" && (<>
                  <button className="mini-button" onClick={() => setEditing(x)} data-testid={`wd-edit-${x.withdrawal_id}`}>Edit</button>
                  <button className="mini-button success" onClick={() => act(x.withdrawal_id, "completed")} data-testid={`wd-complete-${x.withdrawal_id}`}>Complete</button>
                </>)}
                {canDelete(x.status) && (
                  <button className="mini-button danger" onClick={() => delOne(x.withdrawal_id)} data-testid={`wd-delete-${x.withdrawal_id}`}>Delete</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!items.length && <div className="empty-state"><b>No withdrawal requests</b></div>}
      {editing && <WithdrawalEditModal wd={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); refresh(); }} />}
    </section>
  );
}

function WithdrawalEditModal({ wd, onClose, onSaved }) {
  const [form, setForm] = useState({
    address: wd.address || "",
    account_name: wd.account_name || "",
    bank_account_name: wd.bank_account_name || "",
    bank_account_number: wd.bank_account_number || "",
    bank_ifsc: wd.bank_ifsc || "",
    reason: "",
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (form.reason.trim().length < 3) { setErr("Reason is required (min 3 chars)."); return; }
    setSaving(true); setErr("");
    try {
      const payload = { reason: form.reason.trim() };
      if (wd.network === "BEP20") payload.address = form.address.trim();
      if (wd.network === "UPI") { payload.address = form.address.trim(); payload.account_name = form.account_name.trim(); }
      if (wd.network === "BANK") {
        payload.bank_account_name = form.bank_account_name.trim();
        payload.bank_account_number = form.bank_account_number.trim();
        payload.bank_ifsc = form.bank_ifsc.trim().toUpperCase();
        payload.address = form.bank_account_number.trim();
      }
      await api.post(`/admin/withdrawals/${wd.withdrawal_id}/edit`, payload);
      onSaved();
    } catch (e) { setErr(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="wd-edit-modal">
      <form className="panel modal" onClick={(e) => e.stopPropagation()} onSubmit={save}>
        <button className="modal-close" onClick={onClose} type="button" aria-label="Close"><X size={18} /></button>
        <div className="eyebrow">EDIT DESTINATION</div>
        <h3>Fix wrong {wd.network} details · {wd.withdrawal_id}</h3>
        <p className="panel-copy small">User gets a notification whenever the destination is edited. The reason is stored in the audit log.</p>
        {err && <div className="error-box" data-testid="wd-edit-error">{err}</div>}
        {wd.network === "BEP20" && (
          <label>BEP20 wallet address
            <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="wd-edit-address" />
          </label>
        )}
        {wd.network === "UPI" && (
          <>
            <label>Account holder name
              <input required value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} data-testid="wd-edit-upi-name" />
            </label>
            <label>UPI ID
              <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="wd-edit-upi-id" />
            </label>
          </>
        )}
        {wd.network === "BANK" && (
          <>
            <label>Account holder name
              <input required value={form.bank_account_name} onChange={(e) => setForm({ ...form, bank_account_name: e.target.value })} data-testid="wd-edit-bank-name" />
            </label>
            <label>Account number
              <input required value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} data-testid="wd-edit-bank-acct" />
            </label>
            <label>IFSC
              <input required value={form.bank_ifsc} onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value.toUpperCase() })} data-testid="wd-edit-bank-ifsc" />
            </label>
          </>
        )}
        <label>Reason for edit (shared with user)
          <input required minLength={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="wd-edit-reason" placeholder="e.g. User asked to correct UPI ID over support" />
        </label>
        <div className="button-row">
          <button type="submit" className="button primary" disabled={saving} data-testid="wd-edit-save">{saving ? "Saving…" : "Save changes"}</button>
          <button type="button" className="button ghost" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

const emptyPlan = { name: "", min_amount: 100, max_amount: 5000, duration_days: 30, daily_rate_percent: 0.2, status: "Active", visible: true };

function PlanLogoUpload({ plan, onDone }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);
  const url = plan.logo_path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${plan.logo_path}` : null;
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", f);
    api.post(`/admin/plans/${plan.plan_id}/logo`, form, { headers: { "Content-Type": "multipart/form-data" } })
      .then(() => onDone())
      .catch((err) => window.alert(err.response?.data?.detail || "Upload failed"))
      .finally(() => { setUploading(false); if (inputRef.current) inputRef.current.value = ""; });
  };
  return (
    <div className="plan-logo-cell">
      <span className="plan-logo-round admin">
        {url ? <img src={url} alt="" /> : <b>{plan.name?.charAt(0) || "N"}</b>}
      </span>
      <button type="button" className="mini-button" onClick={() => inputRef.current?.click()}
        disabled={uploading} data-testid={`plan-logo-upload-${plan.plan_id}`}>
        {uploading ? "Uploading…" : url ? "Change" : "Upload"}
      </button>
      <input ref={inputRef} type="file" accept="image/*" onChange={pick} style={{display:"none"}} />
    </div>
  );
}

function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/plans").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...editing };
    ["min_amount", "max_amount", "duration_days", "daily_rate_percent"].forEach((k) => (payload[k] = Number(payload[k])));
    if (editing.plan_id) await api.put(`/admin/plans/${editing.plan_id}`, payload);
    else await api.post("/admin/plans", payload);
    setEditing(null); load();
  };
  const del = async (id) => { if (window.confirm("Delete this plan?")) { await api.delete(`/admin/plans/${id}`); load(); } };
  return (
    <section className="panel wide">
      <div className="panel-head">
        <div><div className="eyebrow">PLANS</div><h2>Investment plans</h2></div>
        <button className="button primary" onClick={() => setEditing(emptyPlan)} data-testid="plan-new-button"><ArrowUpRight size={14} /> New plan</button>
      </div>
      <table className="data-table">
        <thead><tr><th>Logo</th><th>Name</th><th>Range</th><th>Duration</th><th>Rate</th><th>Status</th><th>Visible</th><th /></tr></thead>
        <tbody>
          {plans.map((p) => (
            <tr key={p.plan_id} data-testid={`admin-plan-${p.plan_id}`}>
              <td>
                <PlanLogoUpload plan={p} onDone={load} />
              </td>
              <td>{p.name}</td><td>{money(p.min_amount)} – {money(p.max_amount)}</td>
              <td>{p.duration_days}d</td><td>{p.daily_rate_percent}% / day</td>
              <td><span className={`status ${p.status === "Active" ? "active" : "paused"}`}>{p.status}</span></td>
              <td>{p.visible ? "Yes" : "No"}</td>
              <td>
                <button className="mini-button" onClick={async () => { await api.post(`/admin/plans/${p.plan_id}/toggle-status`); load(); }} data-testid={`plan-toggle-${p.plan_id}`}>
                  {p.status === "Active" ? "Pause" : "Resume"}
                </button>
                <button className="mini-button" onClick={() => setEditing(p)} data-testid={`plan-edit-${p.plan_id}`}>Edit</button>
                <button className="mini-button danger" onClick={() => del(p.plan_id)} data-testid={`plan-delete-${p.plan_id}`}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editing && (
        <form className="panel edit-panel" onSubmit={save}>
          <h3>{editing.plan_id ? "Edit plan" : "New plan"}</h3>
          <div className="grid-two">
            <label>Name<input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="plan-name-input" /></label>
            <label>Status
              <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="plan-status-input">
                <option>Active</option><option>Paused</option><option>Closed</option>
              </select>
            </label>
            <label>Min amount<input type="number" required value={editing.min_amount} onChange={(e) => setEditing({ ...editing, min_amount: e.target.value })} data-testid="plan-min-input" /></label>
            <label>Max amount<input type="number" required value={editing.max_amount} onChange={(e) => setEditing({ ...editing, max_amount: e.target.value })} data-testid="plan-max-input" /></label>
            <label>Duration (days)<input type="number" required value={editing.duration_days} onChange={(e) => setEditing({ ...editing, duration_days: e.target.value })} data-testid="plan-duration-input" /></label>
            <label>Daily rate %<input type="number" step="0.01" required value={editing.daily_rate_percent} onChange={(e) => setEditing({ ...editing, daily_rate_percent: e.target.value })} data-testid="plan-rate-input" /></label>
            <label className="checkbox">
              <input type="checkbox" checked={editing.visible} onChange={(e) => setEditing({ ...editing, visible: e.target.checked })} data-testid="plan-visible-input" /> Visible to users
            </label>
          </div>
          <div className="button-row">
            <button className="button primary" data-testid="plan-save-button">Save</button>
            <button type="button" className="button ghost" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </form>
      )}
    </section>
  );
}

function AdminReferrals() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/referrals").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="loading">Loading referrals…</div>;
  return (
    <section className="panel wide">
      <div className="eyebrow">REFERRALS</div>
      <h2>Program overview</h2>
      <div className="metric-grid four">
        <Metric icon={<Users />} label="Referred users" value={data.total_referred_users} />
        <Metric icon={<Wallet />} label="Total paid" value={money(data.total_rewards_paid)} accent />
        <Metric icon={<Activity />} label="Rewards logged" value={data.rewards.length} />
        <Metric icon={<TrendingUp />} label="Avg reward" value={money(data.rewards.length ? data.total_rewards_paid / data.rewards.length : 0)} />
      </div>
      <h3 className="section-sub">Recent rewards</h3>
      <table className="data-table">
        <thead><tr><th>Reward</th><th>Referrer</th><th>Referred</th><th>Percent</th><th>Amount</th><th>Date</th></tr></thead>
        <tbody>{data.rewards.map((r) => (
          <tr key={r.reward_id}><td>{r.reward_id}</td><td>{r.referrer_user_id}</td><td>{r.referred_user_id}</td><td>{r.percent}%</td><td className="pos">{money(r.amount)}</td><td>{fmtDT(r.created_at)}</td></tr>
        ))}</tbody>
      </table>
      {!data.rewards.length && <div className="empty-state"><b>No rewards yet</b></div>}
    </section>
  );
}

function AdminBroadcast() {
  const [form, setForm] = useState({ title: "", body: "" });
  const [msg, setMsg] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/admin/broadcast", form);
      setMsg(`Sent to ${r.data.sent} users.`);
      setForm({ title: "", body: "" });
      setTimeout(() => setMsg(""), 2600);
    } catch (err) { setMsg(err.response?.data?.detail || "Broadcast failed"); }
  };
  return (
    <section className="panel wide">
      <div className="eyebrow">BROADCAST</div>
      <h2>Send announcement to all users</h2>
      <p className="panel-copy">Delivers as a notification to every non-admin account. Use for maintenance windows, new plans, or important reminders.</p>
      {msg && <div className="success-box" data-testid="broadcast-message"><CheckCircle2 /> {msg}</div>}
      <form className="form-stack" onSubmit={submit}>
        <label>Title
          <input required maxLength={80} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="broadcast-title-input" />
        </label>
        <label>Message
          <textarea required rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="broadcast-body-input" />
        </label>
        <button className="button primary" data-testid="broadcast-send-button"><Bell size={14} /> Send to all users</button>
      </form>
    </section>
  );
}

function AdminAdmins() {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [msg, setMsg] = useState("");
  const load = () => api.get("/admin/admins").then((r) => setAdmins(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/admins", form);
      setMsg("Admin created."); setForm({ email: "", password: "", name: "" }); load();
      setTimeout(() => setMsg(""), 2500);
    } catch (err) { setMsg(err.response?.data?.detail || "Could not create admin"); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this admin?")) return;
    try { await api.delete(`/admin/admins/${id}`); load(); }
    catch (err) { setMsg(err.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="content-grid stack">
      <section className="panel wide">
        <div className="eyebrow">ADMIN TEAM</div>
        <h2>Current admins</h2>
        {msg && <div className="success-box" data-testid="admin-team-message"><CheckCircle2 /> {msg}</div>}
        <table className="data-table">
          <thead><tr><th>ID</th><th>Name</th><th>Email</th><th>Joined</th><th /></tr></thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.user_id} data-testid={`admin-row-${a.user_id}`}>
                <td>{a.user_id}</td><td>{a.name}</td><td>{a.email}</td>
                <td>{fmtDT(a.created_at)}</td>
                <td><button className="mini-button danger" onClick={() => del(a.user_id)} data-testid={`admin-delete-${a.user_id}`}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="panel wide">
        <div className="eyebrow">ADD ADMIN</div>
        <h2>Grant admin access</h2>
        <p className="panel-copy small">New admins can approve deposits, edit plans, and manage users. Choose a strong password.</p>
        <form className="form-stack" onSubmit={create}>
          <div className="grid-two">
            <label>Full name
              <input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-admin-name" />
            </label>
            <label>Email
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-admin-email" />
            </label>
            <label>Password (min 8)
              <input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-admin-password" />
            </label>
          </div>
          <button className="button primary" data-testid="new-admin-submit"><LockKeyhole size={14} /> Create admin</button>
        </form>
      </section>
    </div>
  );
}

function AdminInvestments() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("active");
  const [rateFor, setRateFor] = useState(null);
  const [closeFor, setCloseFor] = useState(null);
  const [rateForm, setRateForm] = useState({ daily_rate_percent: "", reason: "" });
  const [closeForm, setCloseForm] = useState({ reason: "" });
  const [msg, setMsg] = useState("");
  const load = useCallback(() => api.get(`/admin/investments${status ? `?status=${status}` : ""}`).then((r) => setItems(r.data)), [status]);
  useEffect(() => { load(); }, [load]);
  const submitRate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/investments/${rateFor.investment_id}/rate`, {
        daily_rate_percent: Number(rateForm.daily_rate_percent), reason: rateForm.reason,
      });
      setMsg(`Rate updated for ${rateFor.investment_id}`);
      setRateFor(null); setRateForm({ daily_rate_percent: "", reason: "" }); load();
      setTimeout(() => setMsg(""), 2500);
    } catch (err) { setMsg(err.response?.data?.detail || "Failed"); }
  };
  const submitClose = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post(`/admin/investments/${closeFor.investment_id}/close`, { reason: closeForm.reason });
      setMsg(`Investment closed. Refunded $${r.data.refunded}`);
      setCloseFor(null); setCloseForm({ reason: "" }); load();
      setTimeout(() => setMsg(""), 3000);
    } catch (err) { setMsg(err.response?.data?.detail || "Failed"); }
  };
  return (
    <section className="panel wide">
      <div className="panel-head">
        <div><div className="eyebrow">INVESTMENTS</div><h2>All stakes</h2></div>
        <select className="search-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-investment-filter">
          <option value="">All</option><option value="active">Active</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
        </select>
      </div>
      {msg && <div className="success-box" data-testid="investment-message"><CheckCircle2 /> {msg}</div>}
      <table className="data-table">
        <thead><tr><th>ID</th><th>User</th><th>Plan</th><th>Amount</th><th>Rate</th><th>Duration</th><th>Status</th><th /></tr></thead>
        <tbody>
          {items.map((x) => (
            <tr key={x.investment_id} data-testid={`admin-inv-${x.investment_id}`}>
              <td>{x.investment_id}</td><td>{x.user_id}</td><td>{x.plan_name}</td>
              <td>{money(x.amount)}</td><td>{x.daily_rate_percent}% / day</td>
              <td>{x.duration_days}d</td>
              <td><span className={`status ${x.status}`}>{x.status}</span></td>
              <td>
                {x.status === "active" && (
                  <>
                    <button className="mini-button" onClick={() => { setRateFor(x); setRateForm({ daily_rate_percent: x.daily_rate_percent, reason: "" }); }} data-testid={`inv-rate-${x.investment_id}`}>Change rate</button>
                    <button className="mini-button danger" onClick={() => setCloseFor(x)} data-testid={`inv-close-${x.investment_id}`}>Force close</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!items.length && <div className="empty-state"><b>No investments</b></div>}
      {rateFor && (
        <div className="modal-backdrop" onClick={() => setRateFor(null)}>
          <form className="panel modal" onClick={(e) => e.stopPropagation()} onSubmit={submitRate}>
            <div className="eyebrow">CHANGE DAILY RATE</div>
            <h3>{rateFor.investment_id} · {rateFor.plan_name}</h3>
            <p className="panel-copy small">Current: <b>{rateFor.daily_rate_percent}% / day</b>. New rate applies from now onwards through the remaining duration.</p>
            <label>New daily rate %
              <input type="number" step="0.01" required min="0" value={rateForm.daily_rate_percent}
                onChange={(e) => setRateForm({ ...rateForm, daily_rate_percent: e.target.value })}
                data-testid="rate-percent-input" />
            </label>
            <label>Reason
              <input required value={rateForm.reason} onChange={(e) => setRateForm({ ...rateForm, reason: e.target.value })} data-testid="rate-reason-input" placeholder="e.g. VIP promo, correction" />
            </label>
            <div className="button-row">
              <button className="button primary" data-testid="rate-submit-button">Apply new rate</button>
              <button type="button" className="button ghost" onClick={() => setRateFor(null)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {closeFor && (
        <div className="modal-backdrop" onClick={() => setCloseFor(null)}>
          <form className="panel modal" onClick={(e) => e.stopPropagation()} onSubmit={submitClose}>
            <div className="eyebrow">FORCE CLOSE INVESTMENT</div>
            <h3>{closeFor.investment_id} · {closeFor.plan_name}</h3>
            <p className="panel-copy small">Principal <b>{money(closeFor.amount)}</b> will be refunded to the user's balance. Earned interest is forfeited. This action is logged.</p>
            <label>Reason
              <input required value={closeForm.reason} onChange={(e) => setCloseForm({ reason: e.target.value })} data-testid="close-reason-input" placeholder="e.g. User request, KYC block, compliance" />
            </label>
            <div className="button-row">
              <button className="button primary" data-testid="close-submit-button">Confirm force close</button>
              <button type="button" className="button ghost" onClick={() => setCloseFor(null)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

function AdminAccess() {
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const load = () => api.get("/admin/login-history").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const unlock = async (target) => {
    const params = new URLSearchParams(target);
    try {
      const r = await api.post(`/admin/login-history/unlock?${params.toString()}`);
      setMsg(`Cleared ${r.data.cleared} failed attempts. Lock removed.`);
      load(); setTimeout(() => setMsg(""), 3000);
    } catch (err) { setMsg(err.response?.data?.detail || "Unlock failed"); }
  };
  // Aggregate active locks (5+ recent failures for email or IP)
  const cutoff = Date.now() - 15 * 60 * 1000;
  const emailCounts = {}, ipCounts = {};
  rows.forEach((r) => {
    if (r.success || r.blocked) return;
    if (new Date(r.created_at).getTime() < cutoff) return;
    if (r.email) emailCounts[r.email] = (emailCounts[r.email] || 0) + 1;
    if (r.ip) ipCounts[r.ip] = (ipCounts[r.ip] || 0) + 1;
  });
  const locked = [
    ...Object.entries(emailCounts).filter(([, n]) => n >= 5).map(([e]) => ({ type: "email", value: e })),
    ...Object.entries(ipCounts).filter(([, n]) => n >= 5).map(([i]) => ({ type: "ip", value: i })),
  ];
  return (
    <section className="panel wide">
      <div className="eyebrow">SECURITY</div>
      <h2>Admin sign-in history</h2>
      <p className="panel-copy">Every attempt is logged. Locks are automatic after 5 failures in 15 minutes (per email or IP).</p>
      {msg && <div className="success-box" data-testid="access-message"><CheckCircle2 /> {msg}</div>}
      {locked.length > 0 && (
        <div className="lock-banner" data-testid="active-locks">
          <b>Active locks:</b>
          {locked.map((l, i) => (
            <span key={i} className="lock-tag">
              {l.type}: <code>{l.value}</code>
              <button className="mini-button" onClick={() => unlock({ [l.type]: l.value })} data-testid={`unlock-${l.type}-${l.value}`}>Unlock</button>
            </span>
          ))}
        </div>
      )}
      <table className="data-table">
        <thead><tr><th>Log</th><th>Email</th><th>Result</th><th>IP</th><th>User agent</th><th>When</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.login_id} data-testid={`login-row-${r.login_id}`}>
            <td>{r.login_id}</td><td>{r.email}</td>
            <td>
              {r.success
                ? <span className="status active">success</span>
                : r.blocked
                  ? <span className="status paused">blocked</span>
                  : <span className="status rejected">failed</span>}
            </td>
            <td className="mono">{r.ip || "—"}</td>
            <td className="mono" title={r.user_agent}>{(r.user_agent || "").slice(0, 40)}{(r.user_agent || "").length > 40 ? "…" : ""}</td>
            <td>{new Date(r.created_at).toLocaleString()}</td>
          </tr>
        ))}</tbody>
      </table>
      {!rows.length && <div className="empty-state"><b>No sign-in attempts yet</b></div>}
    </section>
  );
}

function AdminSettings() {
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setForm(r.data)); }, []);
  if (!form) return <div className="loading">Loading settings…</div>;
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    ["min_deposit", "min_withdrawal", "withdrawal_fee_percent", "referral_percent", "referral_l2_percent", "welcome_bonus_percent", "withdrawal_cooldown_hours"].forEach((k) => (payload[k] = Number(payload[k])));
    payload.manual_inr_rate = payload.manual_inr_rate === "" || payload.manual_inr_rate == null ? null : Number(payload.manual_inr_rate);
    const r = await api.put("/admin/settings", payload); setForm(r.data); setMsg("Settings saved.");
    setTimeout(() => setMsg(""), 2200);
  };
  const uploadQr = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/settings/qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, deposit_qr_path: r.data.deposit_qr_path }));
      setMsg("QR uploaded."); setTimeout(() => setMsg(""), 2200);
    } catch (err) { setMsg(err.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };
  const qrUrl = form.deposit_qr_path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${form.deposit_qr_path}` : "";
  const field = (label, key, type = "text") => (
    <label>{label}
      {type === "textarea" ? (
        <textarea rows={3} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`settings-${key}`} />
      ) : type === "checkbox" ? (
        <input type="checkbox" checked={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.checked })} data-testid={`settings-${key}`} />
      ) : (
        <input type={type} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`settings-${key}`} />
      )}
    </label>
  );
  return (
    <section className="panel wide">
      <div className="eyebrow">SETTINGS</div>
      <h2>Platform configuration</h2>
      {msg && <div className="success-box" data-testid="settings-message"><CheckCircle2 /> {msg}</div>}
      <form className="form-stack" onSubmit={save}>
        <div className="grid-two">
          {field("Site name", "site_name")}
          {field("Contact email", "contact_email")}
          {field("USDT network", "network")}
          {field("Deposit address", "deposit_address")}
          {field("Memo code", "memo_code")}
          {field("Min deposit", "min_deposit", "number")}
          {field("Min withdrawal", "min_withdrawal", "number")}
          {field("Withdrawal fee %", "withdrawal_fee_percent", "number")}
          {field("Referral % (L1 direct)", "referral_percent", "number")}
          {field("Referral % (L2 tier)", "referral_l2_percent", "number")}
          {field("Welcome bonus % (to new user)", "welcome_bonus_percent", "number")}
          {field("Telegram link (for Apply-for-Agent button)", "telegram_link", "text")}
          {field("Withdrawal cooldown (hours between withdrawals)", "withdrawal_cooldown_hours", "number")}
          <label>USDT → INR rate (₹ per 1 USDT) <span style={{color:"#d97e5c"}}>*</span>
            <input type="number" step="0.01" min="0" required value={form.manual_inr_rate ?? ""}
              onChange={(e) => setForm({ ...form, manual_inr_rate: e.target.value })}
              placeholder="e.g. 88.50" data-testid="settings-manual_inr_rate" />
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={!!form.agent_apply_enabled}
              onChange={(e) => setForm({ ...form, agent_apply_enabled: e.target.checked })}
              data-testid="settings-agent_apply_enabled" />
            Show "Apply for agent" button on user profile
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={form.maintenance_mode} onChange={(e) => setForm({ ...form, maintenance_mode: e.target.checked })} data-testid="settings-maintenance_mode" />
            Maintenance mode
          </label>
        </div>
        <div className="qr-upload">
          <div>
            <small>DEPOSIT QR CODE</small>
            {qrUrl ? <img src={qrUrl} alt="QR" className="qr-preview" data-testid="settings-qr-preview" /> : <div className="qr-placeholder">No QR uploaded</div>}
          </div>
          <label className="button ghost qr-btn">
            <input type="file" accept="image/*" onChange={uploadQr} hidden data-testid="settings-qr-upload" />
            {uploading ? "Uploading…" : "Upload / replace QR"}
          </label>
        </div>
        {field("Deposit instructions", "deposit_instructions", "textarea")}
        {field("Withdrawal rules", "withdrawal_rules", "textarea")}
        {field("Risk disclosure", "risk_disclosure", "textarea")}
        <button className="button primary" data-testid="settings-save-button"><SettingsIcon size={14} /> Save settings</button>
      </form>
    </section>
  );
}

function AdminAudit() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setLogs(r.data)); }, []);
  return (
    <section className="panel wide">
      <div className="eyebrow">COMPLIANCE</div>
      <h2>Audit logs</h2>
      <table className="data-table">
        <thead><tr><th>Log</th><th>Actor</th><th>Action</th><th>Entity</th><th>When</th></tr></thead>
        <tbody>{logs.map((l) => (
          <tr key={l.log_id} data-testid={`audit-${l.log_id}`}>
            <td>{l.log_id}</td><td>{l.actor}</td><td>{l.action}</td>
            <td>{l.entity_type} {l.entity_id}</td><td>{new Date(l.created_at).toLocaleString()}</td>
          </tr>
        ))}</tbody>
      </table>
      {!logs.length && <div className="empty-state"><b>No admin actions yet</b></div>}
    </section>
  );
}

// ---------------- Simple page shell ----------------
function Simple({ title, eyebrow, children }) {
  return (
    <main className="simple-page">
      <div className="eyebrow">{eyebrow}</div>
      <h1>{title}</h1>
      {children}
    </main>
  );
}

function AppRoutes({ user, setUser }) {
  const loc = useLocation();
  // Synchronous hash detection — routes ANY page with #session_id to AuthCallback
  if (loc.hash?.includes("session_id=")) return <AuthCallback setUser={setUser} />;
  return (
    <Routes>
      <Route path="/login" element={<Login setUser={setUser} />} />
      <Route path="/admin-login" element={<AdminLogin setUser={setUser} />} />
      <Route path="/dashboard" element={<Dashboard user={user} setUser={setUser} />} />
      <Route path="/admin" element={<Dashboard user={user} setUser={setUser} admin />} />
      <Route path="/" element={<Public user={user} setUser={setUser}>{({ openLogin }) => <Home user={user} onOpenLogin={openLogin} />}</Public>} />
      <Route path="/plans" element={
        <Public user={user} setUser={setUser}>
          <Simple eyebrow="CURATED PATHS" title={<>Plans built for<br /><em>different horizons.</em></>}>
            <p className="lead">Choose a range that matches your timeframe and comfort level. Target figures are not guarantees.</p>
            <PlanCards />
          </Simple>
        </Public>
      } />
      <Route path="/news" element={<Public user={user} setUser={setUser}><NewsPage /></Public>} />
      <Route path="/news/:newsId" element={<Public user={user} setUser={setUser}><NewsDetail /></Public>} />
      <Route path="/about" element={
        <Public user={user} setUser={setUser}>
          <Simple eyebrow="ABOUT NEXA PRO" title={<>A calmer way<br /><em>to move forward.</em></>}>
            <p className="lead">Nexa Pro brings investment planning, transfer visibility, and support into one considered workspace. Trust is built through clarity, not promises.</p>
            <div className="about-grid">
              <div><ShieldCheck /><h3>Designed for transparency</h3><p>Plan ranges, statuses, and review points remain visible. No guaranteed-profit language, ever.</p></div>
              <div><LockKeyhole /><h3>Human-in-the-loop</h3><p>Deposits and withdrawals start pending and require manual review before account changes.</p></div>
              <div><AlertCircle /><h3>Risk disclosure</h3><p>Digital asset activity involves risk. Target returns are informational and never guaranteed. Only invest what you can afford to put at risk.</p></div>
              <div><ClipboardList /><h3>Compliance-ready</h3><p>KYC, AML, and jurisdiction-specific requirements must be reviewed and configured before accepting real customer funds.</p></div>
            </div>
          </Simple>
        </Public>
      } />
      <Route path="/faq" element={
        <Public user={user} setUser={setUser}>
          <Simple eyebrow="HELP CENTER" title={<>Questions deserve<br /><em>clear answers.</em></>}>
            <div className="faq-list">
              {[
                ["How do I register?", "Continue with Google to create a secure Nexa Pro account and receive a unique 8-digit numeric User ID."],
                ["Are returns guaranteed?", "No. Any target figures are informational only. Digital asset activity involves risk."],
                ["How are deposits verified?", "Every USDT deposit starts pending. An admin reviews the transaction hash before crediting a balance."],
                ["Can I change the USDT address?", "Yes. Administrators can update the supported network, deposit address, and memo settings."],
                ["How do withdrawals work?", "Submit a wallet address and amount. The request moves through pending, processing, and completed states after review."],
                ["How does the referral program work?", "Share your unique link. When an invited user completes their first approved deposit, you earn the configured commission."],
                ["Can my account be blocked?", "Administrators may block accounts that violate the terms. You will receive a notification if this happens."],
                ["Where can I see all my activity?", "The Transactions tab in your dashboard shows every ledger movement including deposits, withdrawals, investments, earnings, and referral rewards."],
              ].map(([q, a]) => (
                <details key={q} data-testid={`faq-${q.slice(0, 8).replaceAll(" ", "-")}`}>
                  <summary>{q}<span>+</span></summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
          </Simple>
        </Public>
      } />
    </Routes>
  );
}

function App() {
  const { user, setUser, loaded } = useAuth();
  // Skip /auth/me check if hash contains session_id
  const hasHash = typeof window !== "undefined" && window.location.hash?.includes("session_id=");
  if (!loaded && !hasHash) return <div className="loading">Preparing Nexa Pro…</div>;
  return (
    <BrowserRouter>
      <AppRoutes user={user} setUser={setUser} />
    </BrowserRouter>
  );
}

export default App;
